
import classNames from 'classnames';

import {
	i18n, getWinBB, getDocBB, getTransitionDurationMs,
	getCssValFloat, getReverseBasePos, hasElementScrollBarX, documentQuerySelectorSafe,
	isElementScrollableX, isBoundingBoxInsideContainer, hasElementScrollBarY
} from '../dpitUtilsPublic';

import {
	VERSION,
	IS_DEBUG,
	VISUALIZE_BB,
	CLASSES_CORE,
	MODIFIERS,
	EVENTS,
	COMMANDS,
	RESIZE_REACTION,
	REFRESH_REASONS,
	RES_PROCESS_ASYNC,
	CLASSES_CHILD,
	SCROLL_SPEED,
	TIMING,
	SIZE_LIMITS_PX,
	RESIZE_TYPE,
	FEATURES
} from './constants';

import {
	updateCssVar,
	round1dec,
	setTextStyles,
	scrollIntoView,
	setMobileSizeCoef,
	round3dec,
	floor1dec,
	ceil1dec,
	asyncWait,
} from '../../../../../includes/assets/js/modules/dpitUtilsInc';

import {
	hideElement,
	showElement
} from '../../../../../includes/assets/js/modules/dpitHidden';

import DpitBB from './DpitBB';
import dpitGoldenRatioPosAlg from './dpitGoldenRatioPosAlg';

import BulletsAnimation from '../../../../../includes/assets/js/modules/BulletsAnimation';
import DpitHooks from '../../../../../includes/assets/js/modules/DpitHooks';
import DpitStickyEvents from './DpitStickyEvents';
import DpitColor from '../../../../../includes/assets/js/modules/DpitColor';
import DpitColorAnalyze from '../../../../../includes/assets/js/modules/DpitColorAnalyze';
import DpitTargetBranch from './DpitTargetBranch';
import BrowserSupport from '../../../../../includes/assets/js/modules/BrowserSupport';
import DpitUserInteraction from './DpitUserInteraction';
import EventListeners from './EventListeners';

export default class DpitCore {
	constructor(wrapEl, mainCfg, tourCfg) {
		if (mainCfg.dpDebugEn) this.printDebugMessage();
		this.mainCfg = mainCfg;
		this.tourCfg = tourCfg;
		this.wrapEl = wrapEl;
		this.tourId = tourCfg.tourId;

		this.stepItems = [];
		this.stepItem = null;
		this.prevStepItem = null;
		this.tooltipPadding = {
			left: 20,
			right: 20
		};

		this.isPortraitOrientation = null;
		this.finalizeRefreshCounter = 0;
		this.highlightExtensionPx = 8;
		this.windowMargin = DpitBB.empty(true, false);
		this.docMargin = DpitBB.empty(true, false);
		this.tooltipRadius = 0;

		this.hookBaseArgs = {
			tourId: this.tourId,
			currentUrl: new URL(window.location)
		};

		this.options = {
			dpDebugEn: mainCfg.dpDebugEn,
			labeling: {
				/* Next button label in tooltip box */
				nextLabel: 'Next', // &rarr
				/* Previous button label in tooltip box */
				prevLabel: 'Back',
				/* Cancel button label in tooltip box */
				skipLabel: 'Cancel',
				/* Done button label in tooltip box */
				doneLabel: 'Done',
				button_arrows: 'vector'
			},
			/* Hide previous button always. */
			hide_previous_step_button: false,
			/* Disable navigation to step by click to bullet. */
			disable_navigation_by_bullets: false,
			/* Hide skip button if it is not DONE - in the last step? */
			hide_skip_button: false,
			/* Next CSS class for tooltip box */
			tooltipClass: '',
			/* CSS class that is added to the highlight el */
			highlightClass: '',
			/* Close introduction when pressing Escape button? */
			exitOnEsc: true,
			/* Close introduction when clicking on overlay el? */
			exitOnOverlayClick: true,
			/* Show step numbers in introduction? */
			showStepNumbers: true,
			/* Let user use keyboard to navigate the tour? */
			keyboardNavigation: true,
			/* Show tour bullets? */
			showBullets: true,
			/* Show tour progress? */
			showProgress: false,
			/* Distance of tooltip ... resp tooltip arrow from highlighted box on targeted element */
			tooltipDistanceFromTarget: 8,

			defaultBoardMarginPx: { x: 10, y: 10 },

			targetExtendPaddingPercent: 10,// percents from the target
			targetExtendPaddingMaxPx: 12,

			//Should we scroll the tooltip target element or both?  Options are: 'element', 'tooltip' or all
			scrollTo: 'all',
			scrollSpeed: 20, // %
			scrollSpeedPerPageMs: 300,
			maxScrollSpeedPerPageMs: 1200,

			/* Precedence of positions, when auto is enabled */
			positionPrecedence: ['bottom', 'top', 'right', 'left', 'center'],
			/* Disable an interaction with element? */
			enable_interaction: '1',

			/* additional classes to put on the buttons */
			buttonClass: CLASSES_CORE.button,

			overlayOpacityFull: 0.8,
			overlayOpacityLight: 0.65,
			defaultWinBoardMarginMobSizeCoef: 1,

			centerTopBottomPosOffset: 80,

			max_tooltip_width: 0,

			followTargetIntervalInActionMs: 10,
			followTargetIntervalStandbyMs: 100,
			leaveOverlayAlways: false,
			customTextStylesEn: true,
			...FEATURES
		};
		this.initOverlayOpacity = this.options.overlayOpacityFull;
		this.eventListeners = new EventListeners();
		this.resumeFullOverlayOpacity();
		this.initDebugVisualizer();
		this.actualizeDocAndScreenAreas();
	}

	initUserInteraction() {
		if (FEATURES.userInteraction) {
			this.userInteraction = new DpitUserInteraction(
				this.options,
				this.tooltipRefEl,
				this.inHighlightLayerAnimation,
				this.onUserInteractionIterateStep.bind(this),
				this.onUserInteractionIterateStepInProgress.bind(this),
				this.onBeforeUnloadByLinkClick.bind(this)
			);
		}
	}

	getOnBeforeUnloadByLinkClickConfirmMessage() {
		return i18n('end-a-tour-q', this.mainCfg.i18n);
	}

	onBeforeUnloadByLinkClick(/*href*/) {
		/*if (window.confirm(this.getOnBeforeUnloadByLinkClickConfirmMessage())) {
			window.location.href = href;
		}*/
	}

	onUserInteractionIterateStepInProgress(started) {
		started ? this.disablePointerEvents() : this.enablePointerEvents();
		this.disableStepChanging(started);
	}

	onUserInteractionIterateStep() {
		if (this.isLastStep() && this.userInteraction) {
			this.userInteraction.update(this.stepItem);
		} else {
			this.goToNextStep();
		}
	}

	resetUserInteraction() {
		if (this.userInteraction) {
			this.userInteraction.destroy();
		}
	}

	enableUserInteraction() {
		if (this.userInteraction) {
			this.userInteraction.enable();
		}
	}

	disableUserInteraction(userInteractionTryWhenDisabledFce) {
		if (this.userInteraction) {
			this.userInteraction.disable(userInteractionTryWhenDisabledFce);
		}
	}

	printDebugMessage() {
		console.log('[dpit] INIT DpitCore: v' + VERSION);
	}

	initDebugVisualizer() {
		if (IS_DEBUG && VISUALIZE_BB) {
			import(/* webpackChunkName: "DpitDebugVisualizer" */ './DpitDebugVisualizer').then(module => {
				this.debugVisualizer = new module.default(this.wrapEl);
			});
		}
	}

	doAction(tag, args = {}) {
		if (this.isThisInRun()) {
			DpitHooks.doAction(tag, {
				...this.hookBaseArgs,
				currentStepObj: this.stepObject4Hooks(this.stepItem),
				...args,
			});
		}
	}

	stepObject4Hooks(stepItem) {
		if (!stepItem) return null;
		let res = {
			index: stepItem.step,
			tooltipContent: stepItem.intro,
			tooltipPosition: stepItem.position || 'auto',
			element: this.isDummyMode() ? null : stepItem.element,
			selector: stepItem.selector,
			isEnabledInteraction: stepItem.enable_interaction?.val == 1,
		};
		// PRO features
		if (typeof stepItem.url !== 'undefined') {
			res = {
				...res,
				url: stepItem.url,
				altMobileSelector: stepItem.alt_mobile_selector,
				isInMobileMenu: stepItem.is_in_mobile_menu === '1',
				skipOnBigScreen: stepItem.skip_on_big_screen === '1',
				skipOnMobile: stepItem.skip_on_mobile == 1
			};
		}
		return res;
	}

	startFollowTarget = (insensitivityFirst = 2, insensitivity = 1) => {
		const isDummyModeActive = this.isDummyMode();
		if (/*this.isFixedPos ||*/ this.followTargetInterval || !this.options.followTargetEn || !this.stepItem || (isDummyModeActive && !this.targetDummyBkpEl)) return;
		const isDummyBkpActive = isDummyModeActive && this.targetDummyBkpEl;
		const currentTargetEl = isDummyBkpActive ? this.targetDummyBkpEl : this.stepItem.element;
		if (!currentTargetEl || !this.tooltipRefEl) return;

		let lastTargetBB = DpitBB.fromElement(currentTargetEl);
		const lastRefBB = DpitBB.fromElement(this.tooltipRefEl);
		let lastRefDiffBB = DpitBB.diff(lastTargetBB, lastRefBB);


		this.isFollowTargetEnabled = true;
		let processing = false;



		this.followTargetInterval = setInterval(() => {
			if (!processing && document.hidden /* active tab */
				|| !currentTargetEl
				|| !this.tooltipRefEl
				|| (!$(currentTargetEl).hasClass(CLASSES_CORE.target) && !isDummyBkpActive)
			) return;
			processing = true;
			const insensitivityFinal = !this.isFollowTargetInAction ? insensitivityFirst : insensitivity;

			const newTargetBB = DpitBB.fromElement(currentTargetEl);
			const newRefBB = DpitBB.fromElement(this.tooltipRefEl);
			const newRefDiffBB = DpitBB.diff(newTargetBB, newRefBB);
			const refDiffAbsDiffBB = DpitBB.diffAbs(lastRefDiffBB, newRefDiffBB);
			if (refDiffAbsDiffBB.top > insensitivityFinal
				|| refDiffAbsDiffBB.left > insensitivityFinal
				|| refDiffAbsDiffBB.width > insensitivityFinal
				|| refDiffAbsDiffBB.height > insensitivityFinal
			) {

				if (!this.isFollowTargetInAction) {
					// first follow change
					if (this.options.dpDebugEn) console.log(`[dpit] STEP ${this.stepIdx} FOLLOW TARGET: STARTED - init target BB:`, lastTargetBB);
					this.isFollowTargetInAction = true;
				}
				this.softRefresh(REFRESH_REASONS.followTarget);
				lastRefDiffBB = newRefDiffBB;
				lastTargetBB = newTargetBB;

			}
			processing = false;
		}, this.isFollowTargetInAction ? this.options.followTargetIntervalInActionMs : this.options.followTargetIntervalStandbyMs);
	};

	pauseFollowTarget() {
		if (this.isFollowTargetEnabled) {
			this.wasFollowTargetEnabled = true;
			this.stopFollowTarget();
		}
	}

	restoreFollowTarget() {
		if (!this.isFollowTargetEnabled && this.wasFollowTargetEnabled) {
			this.startFollowTarget();
		}
	}

	pauseResizeRefresh() {
		this.isResizeRefreshPaused = true;
	}

	restoreResizeRefresh() {
		this.isResizeRefreshPaused = false;
	}

	stopFollowTarget() {
		this.isFollowTargetEnabled = false;
		clearInterval(this.followTargetInterval);
		this.followTargetInterval = null;
	}

	restartFollowTarget() {
		this.stopFollowTarget();
		this.startFollowTarget();
	}


	showArrowEl(left, top, tooltipPos, arrowPosId) {
		const arrowPos = { left, top };
		switch (tooltipPos.base) {
			case 'left':
				arrowPos.left = Math.floor(left);
				arrowPos.top = Math.round(top);
				break;
			case 'right':
				arrowPos.left = Math.ceil(left);
				arrowPos.top = Math.round(top);
				break;
			case 'top':
				arrowPos.top = Math.floor(top);
				arrowPos.left = Math.round(left);
				break;
			case 'bottom':
				arrowPos.top = Math.ceil(top);
				arrowPos.left = Math.round(left);
				break;
			default:
				this.hideArrowEl();
				return;
		}

		this.arrowEl.style.left = arrowPos.left + 'px';
		this.arrowEl.style.top = arrowPos.top + 'px';
		this.arrowEl.classList.add(
			`${CLASSES_CORE.arrow}--${arrowPosId}`,
			`${CLASSES_CORE.arrow}--${tooltipPos.align}-aligned`
		);
		showElement(this.arrowEl);
	}

	hideArrowEl() {
		hideElement(this.arrowEl);
		this.arrowEl.removeAttribute('style');
	}

	actualizeArrowEl(left, top, tooltipPos, arrowPos) {
		if (!this.isDummyMode()) this.showArrowEl(left, top, tooltipPos, arrowPos);
		else this.hideArrowEl();
	}


	isDummyMode() {
		return this.targetDummyEl && this.targetDummyEl === this.stepItem.element;
	}



	actualizeDummyMode() {
		const shouldSwitchToDummyMode = this.isDummyMode();
		const targetElementIsNotVisibleAndNotSkipped = this.stepItem.element
			&& !this.options.skip_absent_ref_el
			&& shouldSwitchToDummyMode
			&& !this.isTargetInMobileMenu;
		if (!this.stepItem.element || targetElementIsNotVisibleAndNotSkipped) {
			this.backupDummy(targetElementIsNotVisibleAndNotSkipped);
			this.activateDummyMode();

		}
		else {
			this.deactivateDummyMode();
		}
	}

	backupDummy(targetElementIsNotVisibleAndNotSkipped) {
		this.targetDummyBkpEl = targetElementIsNotVisibleAndNotSkipped ? this.stepItem.element : null;
	}


	activateDummyMode() {
		if (this.stepItem) {
			if (!this.targetDummyEl) {
				this.targetDummyEl = this.createDummyLayer();
				this.wrapEl.appendChild(this.targetDummyEl);
			} else {
				showElement(this.targetDummyEl);
			}
			this.stepItem.element = this.targetDummyEl;
		}
	}

	deactivateDummyMode() {
		hideElement(this.targetDummyEl, false);
		this.targetDummyBkpEl = null;
	}


	updateIndexOfStepItem(stepItem, newZeroBasedIdx) {
		stepItem.step = newZeroBasedIdx + 1;
		stepItem.idx = newZeroBasedIdx;
	}

	initSteps() {
		let stepItems = [];
		if (this.options.steps && Array.isArray(this.options.steps)) {
			this.options.steps.forEach((stepConfig, stepIdx) => {
				const stepItem = this.deepCloneObject(stepConfig);

				//set the step
				stepItem.step = stepIdx + 1;
				stepItem.idx = stepIdx;
				this.updateIndexOfStepItem(stepItem, stepIdx);
				stepItem.selector = stepItem.element;

				this.actualizeTargetItem(stepItem, true);

				if (typeof (stepItem.enable_interaction) === 'undefined') {
					stepItem.enable_interaction = { val: this.options.enable_interaction };
				}
				stepItems.push(stepItem);
			});
		}
		this.stepItems = stepItems;
		return this.stepItems.length > 0;
	}

	/**
	* Start a new intro tour
	*
	* @api private
	* @method startCore
	* @returns {Boolean} Success or not?
	*/
	startCore(isBackward = false, startStep = 0) {
		//set it to the DpitCore object
		if (this.initSteps()) {
			this.startCoreBasic(isBackward, startStep);
		}
	}

	startCoreBasic(isBackward, startStep) {
		this.ending = false;
		this.enableKeyboardNavig();
		this.init();
		this.actualizeScrollSpeedOptions(this.options.scrollSpeed);
		this.actualizeDocAndScreenAreas();
		this.doAction(EVENTS.started);
		this.isPortraitOrientation = this.isScreenPortraitOrientation(this.winBB);
		const startStepNum = Number(startStep);
		if (!this.goToStep(isNaN(startStepNum) ? 0 : startStepNum, isBackward)) {
			this.showBackgroundOverlay();
		}
	}

	onOverlayClick() {
		if (this.options.exitOnOverlayClick === true && this.isRunning) {
			this.exitCoreWithHiddenOverlay('skip');
		}
		this.doAction(EVENTS.onOverlayClick);
	}

	setOption = (option, value) => {
		this.options[option] = value;
	};

	setOptions(options) {
		this.options = {
			...this.options,
			...options
		};
	}

	getOption(name) {
		return this.options[name];
	}

	processBeforeStepChange(newStepItem, prevStepItem) {
		let beforeChangeRes = RES_PROCESS_ASYNC.continue;
		this.finalizeRefreshCounter = 0;
		if (this.beforeStepChangeCallback instanceof Function) {
			beforeChangeRes = this.beforeStepChangeCallback(newStepItem, this.isBackward, prevStepItem);
		}

		return beforeChangeRes;
	}


	async goToStep(stepIdxZeroBased, isBackward = false) {
		if (this.isStepChangingDisabled
			|| !this.stepItems
			|| stepIdxZeroBased < 0
			|| stepIdxZeroBased >= this.stepItems.length) {
			return false;
		}
		await asyncWait(50);

		this.prevStepItem = this.stepItem;
		this.isBackward = isBackward;

		const newStepItem = this.stepItems[stepIdxZeroBased];
		if (this.prevStepItem?.step) {
			this.isBackward = newStepItem.step < this.prevStepItem.step;
		}
		const beforeChangeRes = this.processBeforeStepChange(newStepItem, this.prevStepItem);

		switch (beforeChangeRes) {
			case RES_PROCESS_ASYNC.continue:
				this.doAction(EVENTS.beforeStepChange, {
					nextStepObj: this.stepObject4Hooks(newStepItem),
					backward: this.isBackward,
					currentStepObj: this.stepObject4Hooks(this.prevStepItem)
				}); break;
			case RES_PROCESS_ASYNC.skip: return this.goToStep(stepIdxZeroBased + (this.isBackward ? -1 : 1));
			case RES_PROCESS_ASYNC.abort:
				this.disconnectListeners();
				this.ending = true;
				return false;
		}
		this.resetCoreOnStepChanged();
		this.actualizeCurrentStep(newStepItem);
		$(document.documentElement).stop(true, false);
		this.showStep();
		return true;
	}

	actualizeCurrentStep(newStepItem) {
		if (this.stepItem) $(document.documentElement).removeClass(CLASSES_CORE.stepSpecifier + '-' + this.stepItem.step);
		if (newStepItem) {
			$(document.documentElement).addClass(CLASSES_CORE.stepSpecifier + '-' + newStepItem.step);
			this.stepItem = newStepItem;
			this.stepIdx = newStepItem.idx;
		}
	}

	goToStepByStepLabel = (stepLabel) => {
		const stepNumLabel = Number(stepLabel);
		if (!Number.isNaN(stepNumLabel)) {
			this.goToStep(stepLabel - 1);
		}
	};

	goToPrevStep() {
		return this.goToStep(this.stepIdx - 1);
	}

	goToNextStep() {
		return this.goToStep(this.stepIdx + 1);
	}


	onSkipButtonClick() {
		this.exitCoreWithHiddenOverlay('skip');
	}



	bringOverlayAboveIntroLayers = (cbEnd) => {
		if (this.overlayContainer) this.overlayContainer.classList.add(CLASSES_CORE.overlayContainer + MODIFIERS.above);
		setTimeout(() => {
			if (cbEnd) cbEnd();
		}, this.overlayContainerTransitionDelayMs);
	};

	hideLayers = (byOpacity, noSpace, noTransition, ...args) => {
		if (args.length) {
			args.forEach(layer => {
				hideElement(layer, byOpacity, noSpace, noTransition);
			});
		}
	};

	showLayers = (...args) => {
		if (args.length) {
			args.forEach(layer => { showElement(layer); });
		}
	};

	hideIntroLayers(noSpace = false, byOpacity = true, noTransition = false) {

		this.hideLayers(byOpacity, noSpace, noTransition, this.userInteraction?.disableInteractionLayer, this.tooltipRefEl);
		if (this.highlightEl) {
			this.highlightEl.classList.add(CLASSES_CORE.highlight + MODIFIERS.hidden);
			this.highlightEl.removeAttribute('style');
		}
	}

	hideIntroLayersWithShownBgOverlay(noSpace = false, byOpacity = true, noTransition = false) {
		this.showBackgroundOverlay();
		this.hideIntroLayers(noSpace, byOpacity, noTransition);
	}

	showIntroLayers() {
		if (!this.ending) this.hideBackgroundOverlay();
		if (this.highlightEl) {
			hideElement(this.highlightEl, true, true, true);
			this.highlightEl.classList.remove(CLASSES_CORE.highlight + MODIFIERS.hidden);
		}
		this.showLayers(this.tooltipRefEl, this.highlightEl, this.userInteraction?.disableInteractionLayer, this.wrapEl);
	}

	connectListeners() {
		if (!this.listenersConnected) {
			this.listenersConnected = true;
			this.eventListeners.add(this.overlayEl, 'click', this.onOverlayClick.bind(this));

			this.eventListeners.add(window, 'keydown', this.onKeyDown.bind(this));
			this.eventListeners.add(window, 'resize', this.onResize.bind(this));


			//this.eventListeners.add(document.documentElement, 'scroll wheel DOMMouseScroll mousewheel touchmove', this.stopAutoScrollWhenUserAction.bind(this));
			this.eventListeners.add(document.documentElement, 'scroll', this.stopAutoScrollWhenUserAction.bind(this));
			this.eventListeners.add(document.documentElement, 'wheel', this.stopAutoScrollWhenUserAction.bind(this));
			this.eventListeners.add(document.documentElement, 'DOMMouseScroll', this.stopAutoScrollWhenUserAction.bind(this));
			this.eventListeners.add(document.documentElement, 'mousewheel', this.stopAutoScrollWhenUserAction.bind(this));
			this.eventListeners.add(document.documentElement, 'touchmove', this.stopAutoScrollWhenUserAction.bind(this));

			if (this.buttonCancelEl) this.eventListeners.add(this.buttonCancelEl, 'click', this.onSkipButtonClick.bind(this));
			if (this.buttonPrevEl) this.eventListeners.add(this.buttonPrevEl, 'click', this.goToPrevStep.bind(this));
			if (this.buttonNextEl) this.eventListeners.add(this.buttonNextEl, 'click', this.goToNextStep.bind(this));


		}
	}

	disconnectListeners() {
		if (this.listenersConnected) {
			this.listenersConnected = false;
			this.eventListeners.removeAll();
		}
	}

	finalizeExit() {
		$(this.wrapEl).empty();
		this.highlightEl = null;
		this.arrowIcoWrapData = null;
		this.cancelButtonIcoWrapEl = null;
		this.overlayContainer = null;
		this.stepIdx = undefined;
		this.initialized = false;

	}

	async exitingProcess(leaveActiveOverlay, reason, isRedirect) {
		if (this.targetDummyEl) {
			this.targetDummyEl.parentNode.removeChild(this.targetDummyEl);
			this.targetDummyEl = null;
		}
		this.resetCore(false, false, true);
		this.disableKeyboardNavig();

		if (isRedirect) return;
		this.hideShadowOverlay();
		this.resetCore(true);
		await asyncWait(TIMING.finalizeExit);

		this.doAction(EVENTS.ended, { reason });
		this.finalizeExit();
	}

	exitCoreWithHiddenOverlay(reason) {
		this.exitCore(false, false, reason);
	}

	enablePointerEvents() {
		if (this.wrapEl) this.wrapEl.style.pointerEvents = '';
		if (this.tooltipEl) this.tooltipEl.style.pointerEvents = '';
	}

	disablePointerEvents() {
		if (this.wrapEl) this.wrapEl.style.pointerEvents = 'none';
		if (this.tooltipEl) this.tooltipEl.style.pointerEvents = 'none';
	}

	exitCore = (leaveActiveOverlay = false, force = false, reason = '', isRedirect = false, disableAnimation = false) => {
		this.ending = true;
		if (this.options.leaveOverlayAlways) leaveActiveOverlay = true;

		this.doAction(EVENTS.onBeforeEnd, { reason });

		// calling onBeforeEnd callback
		// If this callback return `false`, it would halt the process
		let continueExit = true;
		if (this.onBeforeEndCallback instanceof Function) {
			continueExit = this.onBeforeEndCallback(this);
		}
		// skip this check if `force` parameter is `true`
		// otherwise, if `onBeforeEnd` returned `false`, don't exit the intro
		if (!force && continueExit === false) return;

		this.disablePointerEvents();
		this.disableKeyboardNavig();
		this.showBackgroundOverlay();
		this.bringOverlayAboveIntroLayers(() => {

			if (!this.tourCfg.properties.disableStartAnimation && !disableAnimation) {

				this.startFullScreenAnimation(true);
				if (!isRedirect) {
					this.stopFullScreenAnimationAfterLoopEnd(() => {
						this.exitingProcess(leaveActiveOverlay, reason, isRedirect);
					});

				}
			}
			else {
				this.exitingProcess(leaveActiveOverlay, reason, isRedirect);
			}
		});
	};



	isInRun() {
		return document.documentElement.classList.contains(CLASSES_CORE.tourInRun);
	}

	isThisInRun() {
		return this.isRunning;
	}

	enableKeyboardNavig() {
		this.keyboardNavigEnabled = true;
	}

	disableKeyboardNavig() {
		this.keyboardNavigEnabled = false;
	}

	disableStepChanging(disable) {
		this.isStepChangingDisabled = disable;
	}

	softenOverlayOpacity(lightOverlayOpacity = this.options.overlayOpacityLight) {
		updateCssVar('--overlay-opacity', lightOverlayOpacity);
	}

	resumeFullOverlayOpacity() {
		updateCssVar('--overlay-opacity', this.options.overlayOpacityFull);
	}

	actualizeHighlightBorder(targetPureBB, numberElBB) {

		let minExtensionSize = { tb: 4, lr: 4 };
		let maxExtensionSize = {
			tb: this.options.targetExtendPaddingMaxPx,
			lr: this.options.targetExtendPaddingMaxPx
		};

		if (numberElBB) {
			minExtensionSize = {
				tb: numberElBB.height / 4,
				lr: numberElBB.width / 4
			};
			maxExtensionSize = {
				tb: numberElBB.height / 3,
				lr: numberElBB.width / 3
			};
		}

		const highlightExtensionSizeBase = {
			tb: Math.ceil(targetPureBB.height * this.options.targetExtendPaddingPercent / 100),
			lr: Math.ceil(targetPureBB.width * this.options.targetExtendPaddingPercent / 100)
		};

		const highlightExtensionSizeTbLr = {
			tb: Math.max(Math.min(highlightExtensionSizeBase.tb, maxExtensionSize.tb), minExtensionSize.tb),
			lr: Math.max(Math.min(highlightExtensionSizeBase.lr, maxExtensionSize.lr), minExtensionSize.lr)
		};

		this.highlightExtensionPx = Math.min(highlightExtensionSizeTbLr.tb, highlightExtensionSizeTbLr.lr);

		const borderWidthCoef = this.stepItem.highlight === 'full_border' ? 1 : 0.5;
		const highlightBorderWidth = Math.ceil(borderWidthCoef * this.highlightExtensionPx);
		updateCssVar('--highlight-border-width', highlightBorderWidth + 'px');
	}

	calculateTargetBBs(actualizeHighlightBorder = true) {

		if (this.stepItem && this.stepItem.element) {

			//const targetPureBox = TheBox.getBox(this.stepItem.element);
			this.targetPureBB = DpitBB.fromElement(this.stepItem.element, this.isFixedPos);
			//console.log(targetPureBox, this.targetPureBB);
			if (actualizeHighlightBorder) {
				this.actualizeHighlightBorder(this.targetPureBB, this.numberElBBSizeStandard);
			}
			this.targetBB = DpitBB.extend(this.targetPureBB, this.highlightExtensionPx);
			if (this.docBB) {
				const intersectionBB = DpitBB.intersect(this.targetBB, this.docBB);
				if (intersectionBB) this.targetBB = intersectionBB;
			}
			this.actualizeBoardSizes();
			if (this.debugVisualizer) this.debugVisualizer.visualizeBoards(/*this.docBoardBB*/this.docBoardBB, this.winBoardBB, this.docBB, this.winBB);
		}
	}


	unsubscribeEvent(eventId, providedCallback) {
		this.subscribeEvent(eventId, providedCallback, true);
	}

	subscribeEvent(eventId, providedCallback, unsubscribe = false) {

		if (typeof (providedCallback) === 'function') {
			if (eventId in EVENTS) {
				if (unsubscribe) DpitHooks.removeAction(eventId, providedCallback);
				else DpitHooks.addAction(eventId, providedCallback);
			}
			else {
				throw new Error(`Provided event name ${eventId} was not recognized. Choose from: ${EVENTS.values().join(',')}`);
			}
		}
		else {
			throw new Error(`Provided callback for ${eventId} was not a function.`);
		}

	}

	pauseRefreshers(all = true) {
		this.pauseFollowTarget();
		if (all) this.pauseResizeRefresh();
	}

	restoreRefreshers() {
		this.restoreFollowTarget();
		this.restoreResizeRefresh();
	}

	startRefreshers() {
		this.startFollowTarget();
		this.restoreResizeRefresh();
	}

	getTourIdPostfix(prefix = CLASSES_CORE.tourInRun) {
		return `${prefix}-${this.tourId}`;
	}


	resetCoreOnStepChanged() {
		this.didOrientationChanged = false;
		this.currentNumberPos = null;
	}

	resetCore(hardReset = true, forceHideLayers = false, doNotHideIntroLayers = false) {
		if ((!IS_DEBUG || forceHideLayers) && !doNotHideIntroLayers) this.hideIntroLayers(hardReset);
		this.clearFixedMode();
		//this.insertDisableInteractionLayer();
		this.pauseRefreshers(hardReset);

		if (hardReset) {
			this.resetUserInteraction();
			this.resetTargetElement();
			this.actualizeCurrentStep(null);
			this.actualizeTheme(null);
			this.disableKeyboardNavig();
			this.disconnectListeners();
			this.clearMobileMenuTarget();
		}
		if (this.debugVisualizer) {
			if (hardReset) this.debugVisualizer.destroy();
			else this.debugVisualizer.clearStepVisualizers();
		}
	}

	unplugTheme() {

	}

	pauseTour(addDocumentClass = '') {
		this.resetCore(true, true);
		document.documentElement.classList.remove(CLASSES_CORE.tourInRun);
		document.documentElement.classList.add(CLASSES_CORE.pause);
		if (addDocumentClass) document.documentElement.classList.add(addDocumentClass);
	}

	isPaused() {
		return document.documentElement.classList.contains(CLASSES_CORE.pause);
	}

	restoreTour(removeDocumentClass = '', reason = REFRESH_REASONS.restore) {
		document.documentElement.classList.add(CLASSES_CORE.tourInRun);
		document.documentElement.classList.remove(CLASSES_CORE.pause);
		if (removeDocumentClass) document.documentElement.classList.remove(removeDocumentClass);
		this.enableKeyboardNavig();
		this.connectListeners();
		this.restoreRefreshers();
		this.hardRefresh(reason);
	}


	setInRunClasses() {
		document.documentElement.classList.add(CLASSES_CORE.tourInRun, this.getTourIdPostfix());
	}

	clearInRunClasses() {
		document.documentElement.classList.remove(CLASSES_CORE.tourInRun, this.getTourIdPostfix());
	}

	/*switchTheme(theme, disableAnimation = false) {

		if (!this.tourCfg.properties.disableStartAnimation && !disableAnimation) {
			this.resetCore(false);
			this.startFullScreenAnimation(true, 1, () => {
				
		
				this.actualizeTheme(theme);
				this.startFullScreenAnimation(false, 1, () => {
					this.restoreTour();
				});
			});
		}
		else {
			this.actualizeTheme(theme);
		}
	}*/


	showShadowOverlay() {
		if (this.highlightEl) this.highlightEl.classList.remove(CLASSES_CORE.highlight + MODIFIERS.noOverlay);
	}

	hideShadowOverlay() {
		if (this.highlightEl) this.highlightEl.classList.add(CLASSES_CORE.highlight + MODIFIERS.noOverlay);
	}

	actualizeShadowOverlay() {
		if (this.hasTargetItemHiddenOverlay(this.stepItem)) {
			this.hideShadowOverlay();
		}
		else {
			this.showShadowOverlay();
		}

		if (this.isDummyMode()) {
			this.highlightEl.classList.add(CLASSES_CORE.highlight + MODIFIERS.dummy);
			this.wrapEl.classList.add(CLASSES_CORE.wrap + MODIFIERS.dummy);
		}
		else {
			this.highlightEl.classList.remove(CLASSES_CORE.highlight + MODIFIERS.dummy);
			this.wrapEl.classList.remove(CLASSES_CORE.wrap + MODIFIERS.dummy);
		}
	}

	showBackgroundOverlay() {
		if (this.overlayContainer) this.overlayContainer.classList.remove(CLASSES_CORE.hiddenKeepEvents);
		//showElement(this.overlayContainer);
		//this.overlayContainer.classList.add(CLASSES_CORE.overlayContainer + MODIFIERS.anim);
	}

	hideBackgroundOverlay() {
		if (this.overlayContainer) this.overlayContainer.classList.add(CLASSES_CORE.hiddenKeepEvents);
		//hideElement(this.overlayContainer, true);
		//this.overlayContainer.classList.remove(CLASSES_CORE.overlayContainer + MODIFIERS.anim);
	}

	startFullScreenAnimation(backward = false, loops = -1, loopsPassedCb = null) {
		this.initBGOverlayLayer();
		this.showBackgroundOverlay();
		this.bulletsAnimation.start(backward, loops, () => {
			if (loopsPassedCb instanceof Function) loopsPassedCb();
		});

	}

	stopFullScreenAnimationAfterLoopEnd(finishCallback = null, hideBackgroundOverlay = true) {
		this.bulletsAnimation.stopAfterLoopEnd(() => {
			if (finishCallback instanceof Function) finishCallback();
			if (hideBackgroundOverlay) this.hideBackgroundOverlay();
		});
	}

	stopFullScreenAnimation() {
		this.bulletsAnimation.stop();
		this.hideBackgroundOverlay();
	}


	setAsRunningTour(callbacksFromSecLayer = {}) {
		if (!window.DpitApi && !this.options.disableApi) window.DpitApi = {
			...callbacksFromSecLayer,
			[COMMANDS.goToNext]: () => this.goToNextStep(),
			[COMMANDS.goToPrev]: () => this.goToPrevStep(),
			[COMMANDS.goToStep]: (stepLabel) => this.goToStepByStepLabel(stepLabel),
			[COMMANDS.exit]: () => this.exitCoreWithHiddenOverlay('skip'),
			[COMMANDS.setAccent]: (color) => this.actualizeAccentColor(color),
			[COMMANDS.getScrollSpeed]: () => this.tourCfg?.properties?.scrollSpeed,
			[COMMANDS.setScrollSpeed]: (scrollSpeedOption) => this.actualizeScrollSpeedOptions(scrollSpeedOption),
			[COMMANDS.setCoreOptions]: (options) => this.setOptions(options),
			[COMMANDS.getCoreOptions]: (key) => this.getOption(key)
		};
		this.setInRunClasses();

		this.isRunning = true;
	}

	resetAsRunningTour() {
		this.clearInRunClasses();
		window.DpitApi = null;
		this.isRunning = false;
	}



	setRadiuses() {
		if (this.tourCfg.tooltip_radius !== '' && this.tourCfg.tooltip_radius >= 0) {
			updateCssVar('--tooltip-border-radius', this.tourCfg.tooltip_radius + 'px');
		}
		if (this.tourCfg.highlight_radius !== '' && this.tourCfg.highlight_radius >= 0) {
			updateCssVar('--highlight-border-radius', this.tourCfg.highlight_radius + 'px');
		}
		if (this.tourCfg.button_radius !== '' && this.tourCfg.button_radius >= 0) {
			updateCssVar('--button-border-radius', this.tourCfg.button_radius + 'px');
			updateCssVar('--button-border-radius-sm', 0.3 * this.tourCfg.button_radius + 'px');
		}
	}


	/**
	 * Actualize tour accent color 
	 *
	 * @param {String|DpitColor} accentColor
	 */
	actualizeAccentColor(accentColorDefinition) {

		if (document.documentElement) {
			if (accentColorDefinition) {

				const accentColor = new DpitColor(accentColorDefinition);
				accentColor.setAsCssVariable('--accent-color');
				const accentContrastColor = DpitColorAnalyze.getContrastColor(accentColor, false);
				accentContrastColor.setAsCssVariable('--accent-contrast-color');
				const accentContrastInvColor = DpitColorAnalyze.getContrastColor(accentContrastColor, false);
				accentContrastInvColor.setAsCssVariable('--accent-contrast-inv-color');

				accentColor.mix(accentContrastColor, 0.2).setAsCssVariable('--accent-color-gentle');
				accentContrastColor.mix(accentContrastInvColor, 0.25).setAsCssVariable('--accent-contrast-color-gentle');
				accentContrastInvColor.mix(accentContrastColor, 0.25).setAsCssVariable('--accent-contrast-inv-color-gentle');
				if (accentContrastColor.isDark()) document.documentElement.classList.add(CLASSES_CORE.lightAccent);
				else document.documentElement.classList.remove(CLASSES_CORE.lightAccent);
				this.tourCfg.accentColor = accentColor;
			} else {
				document.documentElement.style.removeProperty('--accent-color');
				document.documentElement.style.removeProperty('--accent-contrast-color');
				document.documentElement.style.removeProperty('--accent-color-gentle');
				document.documentElement.style.removeProperty('--accent-contrast-inv-color');
				document.documentElement.style.removeProperty('--accent-contrast-color-gentle');
				document.documentElement.style.removeProperty('--accent-contrast-inv-color-gentle');
				document.documentElement.classList.remove(CLASSES_CORE.lightAccent);
			}
		}
	}


	static getThemeClassName(theme) {
		return CLASSES_CORE.themeSpecifier + '-' + theme;
	}

	actualizeThemeSpecifier(newTheme, currentTheme) {
		if (document.documentElement) {
			if (currentTheme) {
				document.documentElement.classList.remove(DpitCore.getThemeClassName(currentTheme));
			}
			if (newTheme) {
				document.documentElement.classList.add(DpitCore.getThemeClassName(newTheme));
			}
		}
	}


	initThemeOptions(themeOverride, accentColorOverride) {
		this.actualizeTheme(themeOverride ?? this.tourCfg.theme);
		this.actualizeAccentColor(accentColorOverride ?? this.tourCfg.accentColor);
		this.setRadiuses(this.tourCfg);
		setTextStyles(this.tourCfg?.text_styles, this.winBB, this.mainCfg?.mobile_break_points?.mobile);
	}



	actualizeTheme(theme, accentColorOverride = null) {
		this.actualizeThemeSpecifier(theme, this.tourCfg.theme);
		if (theme) this.tourCfg.theme = theme;
		this.tooltipPadding = {
			left: getCssValFloat(this.tooltipEl, 'padding-left', 20),
			right: getCssValFloat(this.tooltipEl, 'padding-right', 20)
		};
		this.actualizeAccentColor(accentColorOverride ?? this.tourCfg.accentColor);
	}

	isProgressBarEnabled() {
		return this.options.showProgress && this.stepItems.length > 1;
	}

	isStickyTheme() {
		return document.documentElement.classList.contains('dpit-theme-sticky');
	}


	actualizeWrapperSize(docBB) {
		DpitBB.toElement(this.wrapEl, docBB);
		//this.wrapEl.style.bottom = 
	}

	actualizeDocAndScreenAreas(actualizeBoardSizes = true) {
		this.docBB = getDocBB();
		this.actualizeWrapperSize(this.docBB);
		this.winBB = getWinBB();
		if (!this.oldDocBB) this.oldDocBB = { ...this.docBB };
		this.isMobileSize = this.getIsMobileSize();
		updateCssVar('--highlight-shadow-spread', round1dec(Math.sqrt(Math.pow(this.docBB.width, 2) + Math.pow(this.docBB.height, 2))) + 'px');
		BrowserSupport.vHFix();
		setMobileSizeCoef(this.tourCfg?.text_styles, this.winBB, this.mainCfg?.mobile_break_points?.mobile);
		this.actualizeBulletsAnimationSize();
		if (actualizeBoardSizes) this.actualizeBoardSizes();
	}

	actualizeBoardSizes() {
		this.windowMargin = this.getBoardMargin(this.targetBB, true);
		this.docMargin = this.getBoardMargin(this.targetBB, false);
		this.docBoardBB = this.getBoardBB(true);
		this.actualizeScreenArea();
		this.actualizeTooltipMinWidth();
		if (this.debugVisualizer) this.debugVisualizer.visualizeBoards(this.docBoardBB, this.winBoardBB, this.docBB, this.winBB);
	}

	actualizeScreenArea() {
		this.winBoardBB = this.getBoardBB(false);
	}

	isResizeWithOrientationToggle(currentWinBB, oldWinBB) {
		return this.isScreenPortraitOrientation(currentWinBB) !== this.isScreenPortraitOrientation(oldWinBB);
	}

	isResizeWithMajorAreaChange(currentWinBB, oldWinBB, relativeThreshold = 0.4) {
		const currentArea = DpitBB.area(currentWinBB);
		const oldArea = DpitBB.area(oldWinBB);
		return Math.abs(currentArea - oldArea) > relativeThreshold * currentArea;
	}

	getResizeReactionAndType(currentWinBB, oldWinBB) {
		const isOrientationToggle = oldWinBB && this.isResizeWithOrientationToggle(currentWinBB, oldWinBB);
		if (isOrientationToggle) {
			return { reaction: RESIZE_REACTION.hard, type: RESIZE_TYPE.orientationToggle, isOrientationToggle };
		}
		const isMajorAreaChange = this.isResizeWithMajorAreaChange(currentWinBB, oldWinBB);
		if (isMajorAreaChange) {
			return { reaction: RESIZE_REACTION.hard, type: RESIZE_TYPE.majorAreaChange, isOrientationToggle };
		}
		if (/*currentWinBB.height != this.winBB.height &&*/ currentWinBB.width === this.winBB.width) {
			// Android Chrome address bar 
			return { reaction: RESIZE_REACTION.none, type: RESIZE_TYPE.mobUrlBarVisibilityToggle, isOrientationToggle };
			// TODO getMobileAddressBarHeight
			/*
			if (currentWinBB.height < this.winBB.height) {
				// Android Chrome address bar has appeared
			} else {
				// Android Chrome address bar has disappeared
			}*/
		}
		return { reaction: RESIZE_REACTION.soft, type: RESIZE_TYPE.minorAreaChange, isOrientationToggle };
	}

	actualizeBulletsAnimationSize() {
		if (this.tourCfg?.properties?.bulletAnimSize) {
			const isMobileSize = this.getIsMobileSize(600);
			const size1Bullet = isMobileSize ? this.tourCfg.properties.bulletAnimSize / 20 : this.tourCfg.properties.bulletAnimSize / 30; // include gap
			updateCssVar('--bullet-anim-size', `${size1Bullet}vw`);
			updateCssVar('--bullet-anim-gap', `${round3dec(size1Bullet / 1.1)}vw`);
			updateCssVar('--bullet-anim-border-radius', `${this.tourCfg.properties.bulletAnimBorderRadius}%`);
		}
	}

	resizeRefresh(sizes) {

		const { reaction, type } = this.getResizeReactionAndType(sizes.currentWinSize, sizes.oldWinSize);
		this.winBB = { ...sizes.currentWinSize };
		this.actualizeDocAndScreenAreas();
		if (this.options.dpDebugEn) console.log('[dpit] RESIZE - Sizes:', sizes, 'Reaction:', reaction, 'Type:', type);


		switch (reaction) {
			case RESIZE_REACTION.hard:
				this.doAction(EVENTS.onResize, sizes);
				/*if (target changed due mobile alt target => regularStepChange)
					else */this.hardRefresh(REFRESH_REASONS.resize, { resizeType: type }); break;

			case RESIZE_REACTION.soft:
				this.doAction(EVENTS.onResize, sizes);
				this.softRefresh(REFRESH_REASONS.resize, { resizeType: type });
				break;
			default: this.showIntroLayers();
		}

		//this.wrapEl.classList.remove(CLASSES_CORE.wrap + MODIFIERS.overflowHidden);

	}

	isScreenPortraitOrientation(winBB) {
		return winBB ? winBB.height > winBB.width : false;
	}

	onResize() {
		if (this.options.resizeRefreshEn && !this.isResizeRefreshPaused) {
			//if (this.debugVisualizer) this.debugVisualizer.clearVisualizers();
			const currentWinSize = getWinBB();
			this.actualizeWrapperSize(getDocBB());
			const { reaction, isOrientationToggle } = this.getResizeReactionAndType(currentWinSize, this.oldDocBB);
			if (this.resizeTimeout) {
				this.restartResizeTimeout(reaction, isOrientationToggle, currentWinSize);
			}
			else if (!this.disableOnResize) {
				// First resize event after not resizing
				//if (reaction !== RESIZE_REACTION.none) this.wrapEl.classList.add(CLASSES_CORE.wrap + MODIFIERS.overflowHidden);
				this.disableOnResize = true;
				this.oldWinBB = { ...this.winBB };
				this.oldDocBB = { ...this.docBB };
				this.restartResizeTimeout(reaction, isOrientationToggle, currentWinSize);
				this.disableOnResize = false;
			}
		}
	}

	restartResizeTimeout(reaction, isOrientationToggle, currentWinSize) {
		/*if (reaction === RESIZE_REACTION.none) {
			//this.wrapEl.classList.remove(CLASSES_CORE.wrap + MODIFIERS.overflowHidden);
			if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
			this.resizeTimeout = null;
			this.showIntroLayers();
			return;
		}*/
		//const isOrientationToggle = this.isResizeWithOrientationToggle(currentWinSize, this.oldWinBB);
		const shouldHideIntroLayers = isOrientationToggle || currentWinSize.width !== this.oldWinBB.width;
		//this.pauseFollowTarget();
		if (shouldHideIntroLayers/* && !this.resizeTimeout*/) {
			this.hideIntroLayers(true, true, true);
		}
		if (isOrientationToggle) {
			this.didOrientationChanged = true;
		}

		if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
		this.resizeTimeout = setTimeout(() => {
			this.resizeRefresh({
				oldWinSize: this.oldWinBB,
				currentWinSize: getWinBB(),
				oldSize: this.oldDocBB,
				currentSize: getDocBB()
			});
			this.resizeTimeout = null;
		}, TIMING.resizeReaction);
	}

	isLastStep() {
		return this.stepItems.length - 1 === this.stepIdx;
	}

	onKeyDown(e) {
		if (this.options.keyboardNavigation && this.keyboardNavigEnabled) {
			var code = e.key ?? e.keyCode;
			let isHideNextByCurrentStepMeta = this.isHideNextByCurrentStepMeta();

			// if code/e.which is null
			code ??= e.charCode;
			const isLastStep = this.isLastStep();
			if ((code === 'Escape' || code === 27) && this.options.exitOnEsc === true) {
				//escape key pressed, exit the intro
				//check if exit callback is defined
				this.exitCoreWithHiddenOverlay('skip');
			} else if (code === 'ArrowLeft' || code === 37) {
				//left arrow
				if (!this.options.hide_previous_step_button) this.goToPrevStep();
			} else if (code === 'ArrowRight' || code === 39) {
				//right arrow
				if (!isLastStep && !isHideNextByCurrentStepMeta) this.goToNextStep();
			} else if (code === 'Enter' || code === 13) {
				//srcElement === ie
				const target = e.target || e.srcElement;
				if (!this.options.hide_previous_step_button && target === this.buttonPrevEl) {
					//user hit enter while focusing on previous button
					this.goToPrevStep();
				} else if (target === this.buttonCancelEl) {
					//user hit enter while focusing on skip button
					this.exitCoreWithHiddenOverlay('complete');
				} else if (target && target.getAttribute('data-step-number')) {
					// user hit enter while focusing on step bullet
					target.click();
				} else if (!isLastStep && !isHideNextByCurrentStepMeta) {
					//default behavior for responding to enter
					this.goToNextStep();
				}

				//prevent default behavior on hitting Enter, to prevent steps being skipped in some browsers
				if (e.preventDefault) {
					this.preventDefault(e);
				} else {
					e.returnValue = false;
				}
			}
		}
	}


	preventDefault(event) {
		if (event) {
			event.preventDefault();
		}
	}


	deepCloneObject = (object) => {
		if (object === null || typeof (object) !== 'object' || typeof (object.nodeType) !== 'undefined') {
			return object;
		}
		var temp = {};
		for (var key in object) {
			if (typeof (window.jQuery) !== 'undefined' && object[key] instanceof window.jQuery) {
				temp[key] = object[key];
			} else {
				temp[key] = this.deepCloneObject(object[key]);
			}
		}
		return temp;
	};

	extendBoardMarginByTarget(margin, containerBB, targetBB, filterMarginEn = false) {
		const extendedMargin = filterMarginEn ? this.filterWinBoardMargin(margin) : { ...margin };
		if (!this.isDummyMode() && targetBB) {
			extendedMargin.left = Math.min(extendedMargin.left, targetBB.left);
			extendedMargin.right = Math.min(extendedMargin.right, containerBB.right - 1 - Math.min(targetBB.right, containerBB.right));
			extendedMargin.top = Math.min(extendedMargin.top, targetBB.top);
			extendedMargin.bottom = Math.min(extendedMargin.bottom, containerBB.bottom - 1 - Math.min(targetBB.bottom, containerBB.bottom));
		}

		return extendedMargin;
	}

	filterWinBoardMargin(margin) {
		return margin;
	}

	getBoardMargin(targetBB, filterMarginEn) {
		let mobileSizeCoef = 1;
		if (this.isMobileSize) {
			mobileSizeCoef = this.options.defaultWinBoardMarginMobSizeCoef;
		}
		let margin = {
			left: this.options.defaultBoardMarginPx.x * mobileSizeCoef,
			right: this.options.defaultBoardMarginPx.x * mobileSizeCoef,
			top: this.options.defaultBoardMarginPx.y * mobileSizeCoef,
			bottom: this.options.defaultBoardMarginPx.y * mobileSizeCoef
		};


		margin = this.extendBoardMarginByTarget(margin, this.isFixedPos ? this.winBB : this.docBB, targetBB, filterMarginEn);


		return margin;
	}


	getBoardBB = (isDocumentSize = true) => {

		const margin = isDocumentSize ? this.docMargin : this.windowMargin;
		const container = isDocumentSize ? this.docBB : this.winBB;

		/*this.windowMargins = {
			top: 0,
			right: 0,
			bottom: 0,
			left: 0
		};*/
		let maxROI = {
			top: margin.top,
			right: container.right - margin.right,
			bottom: container.bottom - margin.bottom,
			left: margin.left
		};
		return DpitBB.supplyWidthHeight(maxROI);
	};

	getDesiredNumberPos(tooltipPos) {
		if (this.isDummyMode()) return { x: 'right', y: 'top', onTooltip: true };
		const onTooltip = this.targetBB.height >= this.winBoardBB.height
			|| this.targetBB.width >= this.winBoardBB.width
			|| tooltipPos.base === 'center';
		let desiredNumberPos = { x: 'right', y: 'top', onTooltip: onTooltip };
		// basic position
		if (onTooltip) {
			switch (tooltipPos.base) {
				case 'top': desiredNumberPos.y = 'top'; break;
				case 'right': desiredNumberPos.x = 'right'; break;
				case 'bottom': desiredNumberPos.y = 'bottom'; break;
				case 'left': desiredNumberPos.x = 'left'; break;
			}
		} else {
			switch (tooltipPos.base) {
				case 'top': desiredNumberPos.y = 'bottom'; break;
				case 'right': desiredNumberPos.x = 'left'; break;
				case 'bottom': desiredNumberPos.y = 'top'; break;
				case 'left': desiredNumberPos.x = 'right'; break;
			}
		}
		// align
		switch (tooltipPos.align) {
			case 'left': desiredNumberPos.x = 'right'; break;
			case 'right': desiredNumberPos.x = 'left'; break;
			case 'top': desiredNumberPos.y = 'bottom'; break;
			case 'middle': desiredNumberPos.y = 'top'; break;
			case 'bottom': desiredNumberPos.y = 'top'; break;
			case 'center': desiredNumberPos.x = 'right'; break;
		}
		return desiredNumberPos;
	}



	getArrowData(tooltipPos, arrowHalfSizes, arrowSizes) {
		const tooltipRadius = this.tooltipRadius;
		const arrowElRelPos = {
			top: arrowHalfSizes.height - this.options.tooltipDistanceFromTarget,
			left: this.targetBB.width / 2 - arrowHalfSizes.width
		}; // top, center aligned

		switch (tooltipPos.base) {
			case 'top': arrowElRelPos.top = - arrowHalfSizes.height - 1 - this.options.tooltipDistanceFromTarget; break;
			case 'left': arrowElRelPos.left = - arrowHalfSizes.width - 1 - this.options.tooltipDistanceFromTarget; break;
			case 'bottom': arrowElRelPos.top = this.targetBB.height + this.options.tooltipDistanceFromTarget - arrowHalfSizes.height; break;
			case 'right': arrowElRelPos.left = this.targetBB.width + this.options.tooltipDistanceFromTarget - arrowHalfSizes.width; break;
		}

		let arrowPos = getReverseBasePos(tooltipPos.base);
		if (tooltipPos.base !== 'center') {
			const isLeftOrRight = ['left', 'right'].includes(tooltipPos.base);
			if (isLeftOrRight) {
				arrowElRelPos.top = this.targetBB.height / 2 - arrowHalfSizes.height;
				/*if (this.targetBB.height > this.tooltipBB.height) {
					//arrowElRelPos.top = this.targetBB.height / 2 - this.tooltipBB.height / 2 - arrowHalfSizes.height;
					this.tooltipPos.align = 'middle';
				} else */if (this.tooltipBB.height < 1.8 * this.targetBB.height) {
					switch (tooltipPos.align) {
						case 'bottom':
							arrowElRelPos.top = this.targetBB.height - arrowSizes.height - tooltipRadius - this.options.tooltipDistanceFromTarget;
							break;
						case 'top':
							arrowElRelPos.top = arrowSizes.height + tooltipRadius + this.options.tooltipDistanceFromTarget;
							break;
					}
				} else if (this.targetBB.height > this.tooltipBB.height) {
					//arrowElRelPos.top = this.targetBB.height / 2 - this.tooltipBB.height / 2 - arrowHalfSizes.height;
					this.tooltipPos.align = 'middle';
				}
			}
			else {
				arrowElRelPos.left = this.targetBB.width / 2 - arrowHalfSizes.width;
				if (this.tooltipBB.width < 1.8 * this.targetBB.width) {
					switch (tooltipPos.align) {
						case 'right':
							arrowElRelPos.left = this.targetBB.width - arrowSizes.width - tooltipRadius - this.options.tooltipDistanceFromTarget;
							break;
						case 'left':
							arrowElRelPos.left = arrowSizes.width + tooltipRadius + this.options.tooltipDistanceFromTarget;
							break;
					}
				} else if (this.targetBB.width > this.tooltipBB.width) {
					//arrowElRelPos.left = this.targetBB.width / 2 - this.tooltipBB.width / 2 - arrowHalfSizes.width;
					this.tooltipPos.align = 'center';
				}

				/*if (this.targetBB.width <= this.tooltipBB.width) {
					arrowElRelPos.left = this.targetBB.width / 2 - arrowHalfSizes.width;
				}*/
			}

		}



		return {
			arrowPos,
			arrowElRelPos,
		};
	}

	actualizeTooltipRadiusPadding() {

		this.tooltipRadius = getCssValFloat(this.tooltipEl, 'border-top-right-radius', 0);
		//tooltipRadius = 10000;
		this.radiusPaddingFix = {
			top: round1dec(this.tooltipRadius / 8),
			right: round1dec(this.tooltipRadius / 4),
			bottom: round1dec(this.tooltipRadius / 8),
			left: round1dec(this.tooltipRadius / 4),
		};
	}

	fixArrowInTooltipRadius(tooltipPos, tooltipElRelPos, arrowPos, arrowElRelPos, arrowSizes) {
		const tooltipRadius = this.tooltipRadius;
		//let radiusPaddingCompensation = (tooltipRadius / 8) + 'px ' + (tooltipRadius / 2) + 'px';
		if (this.tooltipPos.base != 'center') {
			// align
			switch (tooltipPos.align) {
				case 'left': if (tooltipRadius > 0 && arrowElRelPos.left < tooltipElRelPos.left + tooltipRadius) {
					this.tooltipEl.style[`border-${arrowPos}-left-radius`] = (Math.max(Math.min(arrowElRelPos.left, tooltipRadius), 0)) + 'px';
				} break;
				case 'right': {
					const tooltipRight = tooltipElRelPos.left + this.tooltipBB.width - 1;
					const arrowRight = arrowElRelPos.left + arrowSizes.width - 1;
					if (arrowRight > tooltipRight - tooltipRadius) {
						this.tooltipEl.style[`border-${arrowPos}-right-radius`] = (Math.max(Math.min(tooltipRight - arrowRight, tooltipRadius), 0)) + 'px';
					}
					break;
				}
				case 'top':
					if (tooltipRadius > 0 && arrowElRelPos.top < tooltipElRelPos.top + tooltipRadius) {
						this.tooltipEl.style[`border-top-${arrowPos}-radius`] = (Math.max(Math.min(arrowElRelPos.top, tooltipRadius), 0)) + 'px';
					}
					break;
				case 'bottom': {
					const tooltipBottom = tooltipElRelPos.top + this.tooltipBB.height - 1;
					const arrowBottom = arrowElRelPos.top + arrowSizes.height - 1;
					if (arrowBottom > tooltipBottom - tooltipRadius) {
						this.tooltipEl.style[`border-bottom-${arrowPos}-radius`] = (Math.max(Math.min(tooltipBottom - arrowBottom, tooltipRadius), 0)) + 'px';
					}
					break;
				}

			}
		}
	}

	actualizeTooltipBB() {
		/*this.tooltipBBRelToTooltipRef = null;
		this.tooltipRefOffset = { top: 0, left: 0 };*/
		this.tooltipBB = DpitBB.fromElement(this.tooltipEl, this.isFixedPos);
		/*if (this.tooltipBB) {
			this.tooltipRefOffset = $(this.tooltipRefEl).offset();
			this.tooltipBBRelToTooltipRef = DpitBB.absoluteToRelative(this.tooltipBB, true, this.tooltipRefOffset.top, this.tooltipRefOffset.left);
		}*/
	}

	actualizeTooltipPosition(relativeLeft, relativeTop) {
		this.tooltipEl.style.left = relativeLeft + 'px';
		this.tooltipEl.style.top = relativeTop + 'px';
		this.actualizeTooltipBB();
	}

	actualizeTooltipSizeAndPos(tooltipPos, tooltipElRelPos) {

		this.actualizeTooltipPosition(tooltipElRelPos.left, tooltipElRelPos.top);
		if (tooltipPos.base) this.tooltipEl.classList.add(`${CLASSES_CORE.tooltip}--${tooltipPos.base}`);
		if (tooltipPos.align) this.tooltipEl.classList.add(`${CLASSES_CORE.tooltip}--${tooltipPos.align}-aligned`);



		if (this.tooltipBB && ['top', 'bottom'].includes(tooltipPos.base)) {

			const maxArea = this.isFixedPos ? this.winBoardBB : this.docBoardBB;
			let diff = 0;

			let isOverLeft = this.tooltipBB.left < maxArea.left;
			let isOverRight = this.tooltipBB.right > maxArea.right;

			if (isOverLeft /*&& this.targetBB.left >maxArea.left*/) {
				diff = Math.abs(maxArea.left - this.tooltipBB.left);
				if (this.options.dpDebugEn) console.log('[dpit] TOOLTIP Pos: Over Left');
				$(this.tooltipEl).offset({ left: maxArea.left });
				this.actualizeTooltipBB();
				isOverRight = this.tooltipBB.right > maxArea.right;
				if (isOverRight) {

					this.tooltipEl.style.width = (this.tooltipBB.width - (2 * diff)) + 'px';
				}
			}
			else if (isOverRight/* && this.targetBB.right <maxArea.right*/) {

				if (this.options.dpDebugEn) console.log('[dpit] TOOLTIP Pos: Over Right');
				const newLeft = maxArea.right - this.tooltipBB.width;
				diff = Math.abs(newLeft - this.tooltipBB.left);
				$(this.tooltipEl).offset({ left: newLeft });
				this.actualizeTooltipBB();
				isOverLeft = this.tooltipBB.left < maxArea.left;
				if (isOverLeft) {
					this.tooltipEl.style.width = (this.tooltipBB.width - (2 * diff)) + 'px';
				}
			}

		}
	}

	filterTooltipElRelPos(elRelPos) {
		return elRelPos;
	}

	placeTooltipCore(
		placeTooltipIterations,
		useLastPosition,
		prevIterationTooltipPos,
		isCrowdedPos
	) {
		let isNeededNextIteration = false;
		const arrowSizes = {
			width: this.arrowEl.offsetWidth,
			height: this.arrowEl.offsetHeight
		};
		const arrowHalfSizes = { width: arrowSizes.width / 2, height: arrowSizes.height / 2 };

		if (!(useLastPosition && this.tooltipPos)) {
			this.tooltipPos = this.determineTooltipPosition(
				this.stepItem,
				arrowSizes,
				isCrowdedPos
			);
		}

		//this.tooltipPos = { base: 'right', align: 'bottom', width: this.tooltipBB.width };

		this.arrowEl.className = CLASSES_CORE.arrow;

		let tooltipElRelPos = { top: 0, left: 0 };
		// basic position
		switch (this.tooltipPos.base) {
			case 'top':
				tooltipElRelPos.top = ceil1dec(-this.tooltipBB.height - arrowHalfSizes.height - this.options.tooltipDistanceFromTarget);
				this.tooltipEl.style.width = '';
				break;
			case 'right':
				tooltipElRelPos.left = floor1dec(this.targetBB.width - 1 + arrowHalfSizes.width + this.options.tooltipDistanceFromTarget); // -1 .. arrow gap
				/*if (placeTooltipIterations === 0)*/ this.tooltipEl.style.width = this.tooltipPos.width + 'px';
				break;
			case 'left':
				tooltipElRelPos.left = ceil1dec(- arrowHalfSizes.width - this.tooltipPos.width - this.options.tooltipDistanceFromTarget);
				/*if (placeTooltipIterations === 0)*/ this.tooltipEl.style.width = this.tooltipPos.width + 'px';
				break;
			case 'bottom':
				tooltipElRelPos.top = floor1dec(this.targetBB.height - 1 + arrowHalfSizes.height + this.options.tooltipDistanceFromTarget); // -1 .. arrow gap
				this.tooltipEl.style.width = '';
				break;
			case 'center':

				this.tooltipEl.style.width = '';
				if (this.isDummyMode()) {
					const stepContent = this.getStepContent(this.stepItem);
					if (stepContent && stepContent.length > 100 && this.tooltipBB.width < 0.4 * this.winBoardBB.width) {
						this.tooltipEl.style.width = 0.4 * this.winBoardBB.width + 'px';
						this.actualizeTooltipBB();
					}
				}
				tooltipElRelPos.left = (this.targetBB.width - this.tooltipBB.width) / 2;

				break;
		}

		// align
		switch (this.tooltipPos.align) {
			case 'center': tooltipElRelPos.left = (this.targetBB.width - this.tooltipBB.width) / 2; break;
			case 'right': {
				let arrowWidthMinusTargetWidth = 0;
				if (arrowSizes.width > this.targetBB.width) {
					arrowWidthMinusTargetWidth = arrowSizes.width - this.targetBB.width;
				}
				tooltipElRelPos.left = this.targetBB.width - this.tooltipBB.width + arrowWidthMinusTargetWidth;
			} break;
			case 'top':
				if (this.tooltipPos.base === 'center') {
					tooltipElRelPos.top = this.options.centerTopBottomPosOffset;
				}
				break;
			case 'middle': tooltipElRelPos.top = (this.targetBB.height - this.tooltipBB.height) / 2; break;
			case 'bottom': {
				let arrowHeightMinusTargetHeight = 0;
				if (arrowSizes.height > this.targetBB.height) {
					arrowHeightMinusTargetHeight = arrowSizes.height - this.targetBB.height;
				}
				tooltipElRelPos.top = this.targetBB.height - this.tooltipBB.height + arrowHeightMinusTargetHeight;
				break;
			}
		}



		tooltipElRelPos = this.filterTooltipElRelPos(tooltipElRelPos);


		this.actualizeTooltipSizeAndPos(this.tooltipPos, tooltipElRelPos);




		const { arrowPos, arrowElRelPos } = this.getArrowData(this.tooltipPos, arrowHalfSizes, arrowSizes);
		this.fixArrowInTooltipRadius(this.tooltipPos, tooltipElRelPos, arrowPos, arrowElRelPos, arrowSizes);
		this.actualizeArrowEl(arrowElRelPos.left, arrowElRelPos.top, this.tooltipPos, arrowPos);


		if (
			this.options.goldenRatioPosAlgEn
			&& this.tooltipBB
			&& !useLastPosition
			&& placeTooltipIterations < 3
			&& !(placeTooltipIterations > 0 && prevIterationTooltipPos.base === this.tooltipPos.base && prevIterationTooltipPos.align === this.tooltipPos.align)

		) {
			let basePositions = ['left', 'right', 'bottom', 'top'].filter(val => !isCrowdedPos[val]);
			if (basePositions.includes(this.tooltipPos.base)) {
				/*const goldenRatioPosAlgRes = */
				dpitGoldenRatioPosAlg(
					this.targetBB,
					this.tooltipBB,
					this.tooltipEl,
					this.tooltipPos,
					this.winBoardBB,
					this.docBoardBB,
					this.isFixedPos,
					this.minTooltipWidth,
					this.options.dpDebugEn//false
				);
				isNeededNextIteration = false;//goldenRatioPosAlgRes.wasPosAdjusted;
				this.actualizeTooltipBB();
				//if (goldenRatioPosAlgRes.isOverHeight) isCrowdedPos[this.tooltipPos.base] = true;
			}
		}
		if (!isNeededNextIteration) {
			const doesNotFitIn = this.fixPositionOutOfTheWindow();
			if (doesNotFitIn) {
				isNeededNextIteration = true;
				isCrowdedPos[this.tooltipPos.base] = true;
			}
		}

		return {
			isNeededNextIteration,
			desiredNumberPos: this.getDesiredNumberPos(this.tooltipPos)
		};
	}


	initTooltip(hard = false) {


		/*if (this.isDummyMode()) {
			this.tooltipEl.classList.add(CLASSES_CORE.tooltip + MODIFIERS.dummy);
		}
		else {
			this.tooltipRefEl.classList.remove(CLASSES_CORE.tooltip + MODIFIERS.dummy);
		}*/


		if (hard) {
			this.tooltipTextEl.classList.remove(CLASSES_CORE.hasScrollBar);
			this.tooltipTextEl.style.height = '';

			this.tooltipEl.className = CLASSES_CORE.tooltip;
			this.tooltipEl.removeAttribute('style');

			//this.tooltipEl.style = '';
			/*this.tooltipEl.style.top = '';
			this.tooltipEl.style.right = '';
			this.tooltipEl.style.bottom = '';
			this.tooltipEl.style.left = '';
			this.tooltipEl.style.width = '';
			this.tooltipEl.style.height = '';*/
			this.actualizeScreenArea();
			this.actualizeTooltipMaxWidth();
		}
		else {
			this.tooltipTextEl.style.maxHeight = '';
			this.tooltipEl.style.marginLeft = '';
			this.tooltipEl.style.marginTop = '';
			this.tooltipEl.style.borderTopLeftRadius = '';
			this.tooltipEl.style.borderTopRightRadius = '';
			this.tooltipEl.style.borderBottomLeftRadius = '';
			this.tooltipEl.style.borderBottomRightRadius = '';
			this.tooltipEl.style.width = '';
		}

		if (this.isDummyMode()) {
			this.tooltipEl.classList.add(CLASSES_CORE.tooltip + MODIFIERS.dummy);
		}
		else {
			this.tooltipEl.classList.remove(CLASSES_CORE.tooltip + MODIFIERS.dummy);
		}



		this.numberEl?.classList.remove(CLASSES_CORE.number + MODIFIERS.onTooltip);

		if (this.stepItem?.hidden_meta?.ext?.build_with_wp_editor === '1') {
			this.tooltipTextEl.classList.add(CLASSES_CORE.tooltipText + MODIFIERS.whiteSpacingNormal);
		}
		else {
			this.tooltipTextEl.classList.remove(CLASSES_CORE.tooltipText + MODIFIERS.whiteSpacingNormal);
		}

		if (this.options.customTextStylesEn) {
			this.tooltipTextEl.classList.add(CLASSES_CORE.tooltipText + MODIFIERS.customStyle);
		}
		else {
			this.tooltipTextEl.classList.remove(CLASSES_CORE.tooltipText + MODIFIERS.customStyle);
		}
	}

	/**
	 * Render tooltip box in the page
	 *
	 * @api private
	 * @method placeTooltip
	 * @param {HTMLElement} targetElement
	 */
	placeTooltip = (useLastTooltipPos = false, useLastNumberPos = false, placeTooltipIterations = 0, isCrowdedPos = {}) => {

		let prevIterationTooltipPos = null;

		if (placeTooltipIterations > 0) {
			prevIterationTooltipPos = { ...this.tooltipPos };
		}

		//prevent error when `this.stepIdx` is undefined
		if (!this.stepItem) return;

		this.actualizeTooltipBB();

		if (typeof (this.options.tooltipClass) === 'string' && this.options.tooltipClass) {
			this.tooltipEl.classList.add(this.options.tooltipClass);
		}
		if (this.isProgressBarEnabled()) {
			this.tooltipEl.classList.add(CLASSES_CORE.tooltip + '--with-progress-bar');
		}

		const { isNeededNextIteration, desiredNumberPos } = this.placeTooltipCore(
			placeTooltipIterations,
			useLastTooltipPos,
			prevIterationTooltipPos,
			isCrowdedPos
		);
		this.desiredNumberPos = desiredNumberPos;

		if (isNeededNextIteration && !useLastTooltipPos && placeTooltipIterations < 2) {
			if (this.options.dpDebugEn && placeTooltipIterations > 0) console.log('[dpit] TOOLTIP Pos. re-calc.', placeTooltipIterations);
			//this.initTooltip(false);
			//this.tooltipEl.style.width = '';
			this.placeTooltip(useLastTooltipPos, false, ++placeTooltipIterations, isCrowdedPos);
		}
		else if (this.actualizeBulletScrollBar()) {
			this.placeTooltip(true, false, ++placeTooltipIterations, isCrowdedPos);
		} else if (this.options.showStepNumbers) {
			this.placeNumber(this.targetBB, this.tooltipPos, desiredNumberPos, useLastNumberPos);
		} else {
			hideElement(this.numberEl, false);
		}

	};


	actualizeStepNumber() {
		this.actualizeStepNumberEl(this.numberEl);
	}


	/** 
	* @param {Element} numberEl - Brief description of the parameter here. Note: For other notations of data types, please refer to JSDocs: DataTypes command.
	*/
	actualizeStepNumberEl(numberEl) {
		const stepNumber = this.stepItem ? this.stepItem.step : '';
		numberEl.firstChild.innerHTML = this.stepItem ? this.stepItem.step : '';
		if (stepNumber > 9) {
			numberEl.classList.add(CLASSES_CORE.number + MODIFIERS['2dig']);
		}
		else {
			numberEl.classList.remove(CLASSES_CORE.number + MODIFIERS['2dig']);
		}
		this.actualizeNumberBB(true);
	}

	actualizeLayer(isEnabled, layerElement, hideByDisp = true, classWhenLayerShown = null) {
		if (isEnabled) {
			showElement(layerElement);
			if (classWhenLayerShown && layerElement) layerElement.classList.add(classWhenLayerShown);
		}
		else {
			hideElement(layerElement, !hideByDisp);
		}
	}


	actualizeNumberBB(takeNumberBBSizeStandardSnapShot = false) {
		if (this.options.showStepNumbers) {
			this.numberBB = DpitBB.fromElement(this.numberEl, this.isFixedPos);
			if (takeNumberBBSizeStandardSnapShot) this.numberElBBSizeStandard = this.takeNumberBBSizeStandardSnapShot();
		}
		else {
			this.numberElBBSizeStandard = null;
		}
	}

	takeNumberBBSizeStandardSnapShot() {
		return { ...this.numberBB };
	}

	getActualNumberPosY(lastPositionKey, lastPositionData, bbNumberDest, indention) {
		let lastPositionKeyBase = lastPositionKey;
		const lastPositionParts = lastPositionKey.split(/(?=[A-Z])/);
		if (lastPositionParts.length) {
			lastPositionKeyBase = lastPositionParts[0];
		}
		let lastPositionBase = null;
		switch (lastPositionKeyBase) {
			case 'top': lastPositionBase = bbNumberDest.top - this.numberBB.height + lastPositionData.numberPosCoef * lastPositionData.numberLayerHalfSize.height; break;
			case 'bottom': lastPositionBase = bbNumberDest.bottom - 1 - lastPositionData.numberPosCoef * lastPositionData.numberLayerHalfSize.height; break;
		}
		switch (lastPositionKey) {
			case 'topIndented': return lastPositionBase + indention;
			case 'bottomIndented': return lastPositionBase - indention;

		}
		return this.isFixedPos ? lastPositionBase + document.documentElement.scrollTop : lastPositionBase;
	}

	getActualNumberPosX(lastPositionKey, lastPositionData, bbNumberDest, indention) {
		let lastPositionKeyBase = lastPositionKey;
		const lastPositionParts = lastPositionKey.split(/(?=[A-Z])/);
		if (lastPositionParts.length) {
			lastPositionKeyBase = lastPositionParts[0];
		}
		let lastPositionBase = null;
		switch (lastPositionKeyBase) {
			case 'left': lastPositionBase = bbNumberDest.left - this.numberBB.width + lastPositionData.numberPosCoef * lastPositionData.numberLayerHalfSize.width; break;
			case 'right': lastPositionBase = bbNumberDest.right - 1 - lastPositionData.numberPosCoef * lastPositionData.numberLayerHalfSize.width; break;
		}

		switch (lastPositionKey) {
			case 'leftIndented': return lastPositionBase + indention;
			case 'rightIndented': return lastPositionBase - indention;

		}

		return this.isFixedPos ? lastPositionBase + document.documentElement.scrollLeft : lastPositionBase;
	}

	placeNumberFast(lastPosition, bbNumberDest) {
		let newPosition = {
			type: lastPosition.type,
			top: this.getActualNumberPosY(lastPosition.type.y, lastPosition, bbNumberDest, lastPosition.indention),
			left: this.getActualNumberPosX(lastPosition.type.x, lastPosition, bbNumberDest, lastPosition.indention),
		};
		$(this.numberEl).offset({ top: newPosition.top, left: newPosition.left });
	}


	getIsPossibleNumberPositionsInBoardObject(possiblePositionsLT) {
		const maxAreaBB = this.isFixedPos ? this.winBB : this.docBB;
		return {
			left: possiblePositionsLT.left >= this.docBB.left && Math.abs(possiblePositionsLT.left - this.tooltipBB.right) < this.winBB.width,
			top: possiblePositionsLT.top >= this.docBB.top && Math.abs(possiblePositionsLT.top - this.tooltipBB.bottom) < this.winBB.height,
			right: possiblePositionsLT.right + this.numberBB.width <= maxAreaBB.right
				&& Math.abs(possiblePositionsLT.right + this.numberBB.width - this.tooltipBB.left) < this.winBB.width,
			bottom: possiblePositionsLT.bottom + this.numberBB.height <= maxAreaBB.bottom
				&& Math.abs(possiblePositionsLT.bottom + this.numberBB.height - this.tooltipBB.top) < this.winBB.height
		};
	}

	getNumberIntersectionFeature(possiblePositionsLT, posX, posY, bbContainerToTest) {
		const numBBToTest = DpitBB.extend(DpitBB.fromLeftTopWidthHeight(
			possiblePositionsLT[posX],
			possiblePositionsLT[posY],
			this.numberBB.width,
			this.numberBB.height
		), 4);

		return DpitBB.intersect(bbContainerToTest, numBBToTest);
	}

	getNumberDestElSizeFeature(numberLayerHalfSize, bbContainerToTest) {
		return { x: numberLayerHalfSize.width / bbContainerToTest.width, y: numberLayerHalfSize.height / bbContainerToTest.height };
	}


	getPlaceNumberRepetitionParams(oldDesiredPosition, iterations) {
		let enableIndentation = iterations >= 2;
		let desiredPosition = { ...oldDesiredPosition, onTooltip: !oldDesiredPosition.onTooltip };
		let maxIterations = 3;
		if (this.isDummyMode()) {
			enableIndentation = true;
			desiredPosition = { ...oldDesiredPosition, onTooltip: true };
			maxIterations = 1;
		}
		return {
			enableIndentation,
			desiredPosition,
			maxIterations
		};
	}

	placeNumber(
		targetBB,
		tooltipPos,
		desiredPosition = { x: 'right', y: 'top', onTooltip: false },
		useLastPosition = false,
		iterations = 1,
		enableIndentation = false) {

		let numberPosCoef = 1;

		let numberLayerHalfSize = { width: this.numberBB.width / 2, height: this.numberBB.height / 2 };


		let numberDestEl = this.highlightEl;
		let bbNumberDest = targetBB;
		let bbOther = this.tooltipBB;

		if (desiredPosition.onTooltip) {
			bbNumberDest = this.tooltipBB;
			bbOther = targetBB;
			numberDestEl = this.tooltipEl;
		} else if (!useLastPosition) {
			//adjust distance of number and target ... higher dist for small targets
			const numberSizeCoef = this.getNumberDestElSizeFeature(numberLayerHalfSize, bbNumberDest);
			const threshold = 0.3;

			if (numberSizeCoef.x > threshold && numberSizeCoef.x < 1.5 || numberSizeCoef.y > threshold && numberSizeCoef.y < 1.5) {
				//enableIndentation = false;
				const numberPosFeatures = { x: 1 - (numberSizeCoef.x - threshold), y: 1 - (numberSizeCoef.y - threshold) };
				numberPosCoef = Math.min(numberPosFeatures.x, numberPosFeatures.y);
				if (this.options.dpDebugEn) console.log('[dpit] NUMBER POS: adjustment - small ref/tooltip. Coef: ', numberPosCoef, 'Features: ', numberPosFeatures);
				//numberSizeCoefCssVar = Math.min(Math.max(1.7 * numberPosCoef, 0.8), 1);
				//this.actualizeNumberBB();
				//numberLayerHalfSize = { width: this.numberBB.width / 2, height: this.numberBB.height / 2 };
			}
		}


		if (useLastPosition && this.currentNumberPos) {
			this.placeNumberFast(this.currentNumberPos, bbNumberDest);
			return;
		}

		this.numberEl.classList.remove(CLASSES_CORE.number + MODIFIERS.hidden);


		if (desiredPosition.onTooltip) {
			this.numberEl.classList.add(CLASSES_CORE.number + MODIFIERS.onTooltip);
		}
		else {
			this.numberEl.classList.remove(CLASSES_CORE.number + MODIFIERS.onTooltip);
		}
		this.actualizeNumberBB(true);

		let possiblePositionsLT = {
			left: bbNumberDest.left - this.numberBB.width + numberPosCoef * numberLayerHalfSize.width + this.radiusPaddingFix.left / 2,
			right: bbNumberDest.right - 1 - numberPosCoef * numberLayerHalfSize.width - this.radiusPaddingFix.right / 2,
			top: bbNumberDest.top - this.numberBB.height + numberPosCoef * numberLayerHalfSize.height + this.radiusPaddingFix.top / 2,
			bottom: bbNumberDest.bottom - 1 - numberPosCoef * numberLayerHalfSize.height - this.radiusPaddingFix.bottom / 2,
		};

		const layerNumberDestRadius = getCssValFloat(numberDestEl, 'border-top-left-radius', 2);
		const indention = layerNumberDestRadius + 4 + numberLayerHalfSize.width;
		if (enableIndentation) {

			possiblePositionsLT = {
				...possiblePositionsLT,
				leftIndented: possiblePositionsLT.left + indention,
				rightIndented: possiblePositionsLT.right - indention,
				topIndented: possiblePositionsLT.top + indention,
				bottomIndented: possiblePositionsLT.bottom - indention,
			};
		}

		let isInWindow = this.getIsPossibleNumberPositionsInBoardObject(possiblePositionsLT);
		const maxAreaBB = this.isFixedPos ? this.winBB : this.docBB;
		if (enableIndentation) {
			isInWindow = {
				...isInWindow,
				leftIndented: possiblePositionsLT.leftIndented <= bbNumberDest.right - indention
					&& possiblePositionsLT.leftIndented >= this.winBB.left,
				topIndented: possiblePositionsLT.topIndented <= bbNumberDest.bottom - indention
					&& possiblePositionsLT.topIndented >= this.winBB.top,
				rightIndented: possiblePositionsLT.rightIndented + this.numberBB.width >= bbNumberDest.left + indention
					&& possiblePositionsLT.rightIndented <= maxAreaBB.right,
				bottomIndented: possiblePositionsLT.bottomIndented + this.numberBB.height >= bbNumberDest.top + indention
					&& possiblePositionsLT.bottomIndented <= maxAreaBB.bottom
			};
		}



		const allowedPositionKeys = Object.keys(possiblePositionsLT).filter((position) => {
			return isInWindow[position];
		});


		let positionKeysY = ['top', 'bottom'];
		let positionKeysX = ['left', 'right'];


		if (enableIndentation) {
			positionKeysY = [...positionKeysY, 'topIndented', 'bottomIndented'];
			positionKeysX = [...positionKeysX, 'leftIndented', 'rightIndented'];
		}

		let finalPositions = [];
		positionKeysY.forEach((posY, idxY) => {
			if (allowedPositionKeys.includes(posY)) {
				positionKeysX.forEach((posX, idxX) => {
					if (allowedPositionKeys.includes(posX)) {
						{
							let score = 0;
							const numberPositionType = { x: posX, y: posY };

							// indented
							if (idxY >= 2) score--;
							if (idxX >= 2) {
								if (posX === 'rightIndented') score -= 0.5; // prefer right indented
								else score--;
							}


							const intersection = this.getNumberIntersectionFeature(possiblePositionsLT, posX, posY, bbOther);

							if (intersection) {
								if (iterations === 1) return;
								score--;
							} else {
								if (desiredPosition.x === posX) score++;
								if (desiredPosition.y === posY) score++;
							}
							finalPositions.push({
								type: numberPositionType,
								left: possiblePositionsLT[posX],
								top: possiblePositionsLT[posY],
								score: score,
								isNumberCloseToTooltip: intersection !== null
							});
						}
					}
				});
			}
		});

		finalPositions.sort((a, b) => {
			return b.score - a.score;
		});


		if (finalPositions.length) {
			this.currentNumberPos = finalPositions[0];
			this.currentNumberPos.numberPosCoef = numberPosCoef;
			this.currentNumberPos.indention = indention;
			this.currentNumberPos.numberLayerHalfSize = numberLayerHalfSize;

			if (this.isFixedPos) {
				this.currentNumberPos.top += document.documentElement.scrollTop;
				this.currentNumberPos.left += document.documentElement.scrollLeft;
			}

			/*if (numberSizeCoefCssVar >= 1 && this.currentNumberPos.isNumberCloseToTooltip) {
				numberSizeCoefCssVar = 0.9;
				if (this.options.dpDebugEn) console.log('[dpit] NUMBER POS: Too close to tooltip');
				//updateCssVar('--num-size-coef', numberSizeCoefCssVar);
				this.actualizeNumberBB();
			}*/

			if (desiredPosition.onTooltip) {
				this.numberEl.classList.add(CLASSES_CORE.number + MODIFIERS.onTooltip);
			}

			$(this.numberEl).offset({ top: this.currentNumberPos.top, left: this.currentNumberPos.left });
			this.actualizeNumberBB();
		} else {
			// was not found
			const nextRunParams = this.getPlaceNumberRepetitionParams(desiredPosition, iterations);
			if (iterations <= nextRunParams.maxIterations) {
				this.placeNumber(targetBB, tooltipPos, nextRunParams.desiredPosition, useLastPosition, ++iterations, nextRunParams.enableIndentation);
			}
			else {
				this.numberEl.classList.add(CLASSES_CORE.number + MODIFIERS.hidden);
				this.currentNumberPos = null;
			}
		}

	}


	isNumberCloseToTooltip(numberPositionType, numberBB, tooltipPosBase, isOnTooltip) {

		const _tooltipPosBase = isOnTooltip ? getReverseBasePos(tooltipPosBase) : tooltipPosBase;
		if (_tooltipPosBase === numberPositionType.x) {
			if (isOnTooltip) {
				if (this.tooltipBB.top + numberBB.height > this.targetBB.top) return true;
				else if (this.tooltipBB.bottom - numberBB.height < this.targetBB.bottom) return true;
			}
			else {
				if (this.tooltipBB.top - numberBB.height < this.targetBB.top) return true;
				else if (this.tooltipBB.bottom + numberBB.height > this.targetBB.bottom) return true;
			}
		}
		else if (_tooltipPosBase === numberPositionType.y) {
			if (isOnTooltip) {
				if (this.tooltipBB.left + numberBB.width > this.targetBB.left) return true;
				else if (this.tooltipBB.right - numberBB.width < this.targetBB.right) return true;
			}
			else {
				if (this.tooltipBB.left - numberBB.width < this.targetBB.left) return true;
				else if (this.tooltipBB.right + numberBB.width > this.targetBB.right) return true;
			}
		}

		return false;
	}


	adjustTooltipTopAfterHeightChange(oldTop, oldHeight, tooltipPos, tooltipEl, middleHeightCoef = 0.5) {
		this.actualizeTooltipScrollBars();
		const newHeight = $(tooltipEl).height();
		const heightDiff = newHeight - oldHeight;
		let newTop = null;
		if (newHeight !== oldHeight) {

			const isLeftOrRight = ['left', 'right'].includes(tooltipPos.base);
			if (isLeftOrRight) {
				switch (tooltipPos.align) {
					case 'middle': newTop = oldTop - heightDiff * middleHeightCoef; break;
					case 'bottom': newTop = oldTop - heightDiff; break;
				}
			} else if (['top', 'center'].includes(tooltipPos.base)) {
				newTop = oldTop - heightDiff;
			}
		}
		return newTop;
	}



	fixOverWinBoardTopForFixedElements(containerBB) {
		const topBkp = getCssValFloat(this.tooltipEl, 'top');
		const tooltipHeightBkp = $(this.tooltipEl).height();
		let tooltipTextHeight = $(this.tooltipTextEl).height();

		const textLayerMaxHeight = Math.floor(Math.max(tooltipTextHeight - (containerBB.top + 1 - this.tooltipBB.top), SIZE_LIMITS_PX.minTooltipContentHeightFixedPos));
		this.tooltipTextEl.style.height = textLayerMaxHeight + 'px';

		const newTop = this.adjustTooltipTopAfterHeightChange(topBkp, tooltipHeightBkp, this.tooltipPos, this.tooltipEl, 1);
		if (newTop) {
			this.tooltipEl.style.top = newTop + 'px';
			//this.tooltipEl.style.top = Math.max(this.docBoardBB.top - this.targetBB.top + 1, newTop) + 'px';
			this.actualizeTooltipScrollBars();
		}
		let isOverTop = this.tooltipBB.top < containerBB.top;
		/*if (isOverTop) {
			$(this.tooltipEl).offset({ top: topBkp });
			this.tooltipTextEl.style.height = '';
			this.actualizeTooltipScrollBars();
			isOverTop = this.tooltipBB.top < containerBB.top;
		}*/
		return isOverTop;
	}


	fixOverWinBoardBottomForFixedElements(containerBB) {
		//console.log('fix over BOTTOM (tooltip on BOTTOM)');
		const tooltipTextHeight = $(this.tooltipTextEl).height();
		const textLayerMaxHeight = Math.floor(Math.max(tooltipTextHeight - (this.tooltipBB.bottom - containerBB.bottom + 1), SIZE_LIMITS_PX.minTooltipContentHeightFixedPos));



		this.tooltipTextEl.style.height = textLayerMaxHeight + 'px';
		this.actualizeTooltipScrollBars();
		let isOverBottom = this.tooltipBB.bottom > containerBB.bottom;
		if (isOverBottom) {
			this.tooltipTextEl.style.height = '';
			this.actualizeTooltipScrollBars();
			isOverBottom = this.tooltipBB.bottom > containerBB.bottom;
		}
		return isOverBottom;
	}

	actualizeTextScrollbar() {
		if (hasElementScrollBarY(this.tooltipTextEl)) {
			this.tooltipTextEl.classList.add(CLASSES_CORE.hasScrollBar);
		} else {
			this.tooltipTextEl.classList.remove(CLASSES_CORE.hasScrollBar);
		}
	}

	actualizeTooltipScrollBars() {
		this.actualizeBulletScrollBar(false);
		this.actualizeTextScrollbar();
		this.actualizeTooltipBB();
	}

	fixPositionOutOfTheBoard(boardBB) {
		//let res = false;
		if (this.tooltipBB.bottom > boardBB.bottom && ['bottom', 'left', 'right', 'center'].includes(this.tooltipPos.base)) { // add center
			/*res |= */this.fixOverWinBoardBottomForFixedElements(boardBB);
		}
		if (this.tooltipBB.top < boardBB.top && ['top', 'left', 'right', 'center'].includes(this.tooltipPos.base)) {  // add center
			/*res |= */this.fixOverWinBoardTopForFixedElements(boardBB);
		}
		return false;
	}


	fixPositionOutOfTheWindowForNotFixedElements() {
		let res = false;
		this.actualizeFlier(true);
		if (this.flierBB) {
			if (this.flierBB.height > this.winBB.height) {
				const topBkp = $(this.tooltipEl).offset().top;
				const tooltipHeightBkp = $(this.tooltipEl).height();
				//console.log('fix over BOTTOM (tooltip on BOTTOM)');
				const tooltipTextHeight = $(this.tooltipTextEl).height();
				if (tooltipTextHeight < SIZE_LIMITS_PX.minTooltipContentHeight) return;
				const requiredHeightDiff = this.winBB.height - this.flierBB.height;

				const textLayerMaxHeight = Math.max(tooltipTextHeight + requiredHeightDiff, SIZE_LIMITS_PX.minTooltipContentHeight);

				this.tooltipTextEl.style.height = textLayerMaxHeight + 'px';
				const newTop = this.adjustTooltipTopAfterHeightChange(topBkp, tooltipHeightBkp, this.tooltipPos, this.tooltipEl);
				if (newTop) {
					$(this.tooltipEl).offset({ top: Math.max(this.winBoardBB.top, newTop) });
					this.actualizeTooltipScrollBars();
				}
				this.actualizeFlier(true);
			}
			res = this.fixPositionOutOfTheBoard(this.docBoardBB);
		}
		return res;
	}


	fixPositionOutOfTheWindow() {
		let res = false;
		if (this.isFixedPos) res = this.fixPositionOutOfTheBoard(this.winBoardBB);
		else res = this.fixPositionOutOfTheWindowForNotFixedElements();
		this.actualizeTextScrollbar();
		return res;
	}

	getDummyTooltipPosition() {
		return { base: 'center', align: 'middle', width: this.tooltipBB.width };
	}

	/**
		 * Determines the position of the tooltip based on the position precedence and availability
		 * of screen space.
		 *
		 */
	determineTooltipPosition(targetItem, arrowSizes, isCrowdedPos) {
		if (this.isDummyMode()) return this.getDummyTooltipPosition();
		let calculatedPosition = { base: 'bottom', align: 'middle', width: this.tooltipBB.width };
		//if (this.isStickyTheme()) return calculatedPosition;
		let desiredTooltipPosition = targetItem.position;

		if (isCrowdedPos[desiredTooltipPosition]) {
			const reversePosition = getReverseBasePos(desiredTooltipPosition);
			if (!isCrowdedPos[reversePosition])
				desiredTooltipPosition = reversePosition;
		}

		const possiblePositionsScoreInc = {};
		this.options.positionPrecedence.forEach((position) => {
			possiblePositionsScoreInc[position] = {
				score: 0,
				disabled: false
			};
		});

		let tooltipSizesArrowInc = {
			width: this.tooltipBB.width + arrowSizes.width / 2 + this.options.tooltipDistanceFromTarget,
			height: this.tooltipBB.height + arrowSizes.height / 2 + this.options.tooltipDistanceFromTarget
		};

		let newTooltipWidthArrowInc = {
			left: tooltipSizesArrowInc.width,
			right: tooltipSizesArrowInc.width
		};
		let newTooltipWidth = {
			left: null,
			right: null
		};

		const maxArea = this.isFixedPos ? this.winBoardBB : this.docBoardBB;

		const rightWidthArrowInc = maxArea.right - this.targetBB.right;
		const rightWidth = rightWidthArrowInc - arrowSizes.width / 2 - this.options.tooltipDistanceFromTarget;
		if (rightWidth > this.minTooltipWidth && rightWidth < this.tooltipBB.width) {
			newTooltipWidthArrowInc.right = rightWidthArrowInc;
			newTooltipWidth.right = rightWidth;
		}

		const leftWidthArrowInc = this.targetBB.left - maxArea.left;
		const leftWidth = leftWidthArrowInc - arrowSizes.width / 2 - this.options.tooltipDistanceFromTarget;
		if (leftWidth > this.minTooltipWidth && leftWidth < this.tooltipBB.width) {
			newTooltipWidthArrowInc.left = leftWidthArrowInc;
			newTooltipWidth.left = leftWidth;
		}

		const minTargetCenterHeight = (desiredTooltipPosition === 'center') ? this.targetBB.height : this.targetBB.height / 2;


		// BOTTOM - check for space below
		let bottomSpaceHardLimit = 0;
		if (this.isFixedPos) {
			bottomSpaceHardLimit = this.winBoardBB.bottom - (this.targetBB.bottom + SIZE_LIMITS_PX.minTooltipHeight); // height can be decreased for fixed to minimal height
		}
		else {
			bottomSpaceHardLimit = this.docBoardBB.bottom - (this.targetBB.bottom + tooltipSizesArrowInc.height);
		}

		possiblePositionsScoreInc['bottom'].disabled = bottomSpaceHardLimit < 0;

		// Score (soft limit)
		if (isCrowdedPos.bottom) possiblePositionsScoreInc['bottom'].score -= 1;
		else {
			let bottomSpaceSoftLimit = this.isFixedPos
				? (this.winBoardBB.bottom - (this.targetBB.bottom + tooltipSizesArrowInc.height))
				: bottomSpaceHardLimit;
			possiblePositionsScoreInc['bottom'].score += bottomSpaceSoftLimit / this.winBoardBB.height;
		}

		// TOP - check for space above
		let topSpaceHardLimit = 0;
		if (this.isFixedPos) {
			topSpaceHardLimit = (this.targetBB.top - SIZE_LIMITS_PX.minTooltipHeight) - this.winBoardBB.top; // height can be decreased for fixed to minimal height
		}
		else {
			topSpaceHardLimit = (this.targetBB.top - tooltipSizesArrowInc.height) - this.docBoardBB.top;
		}
		possiblePositionsScoreInc['top'].disabled = topSpaceHardLimit < 0;

		// Score (soft limit)
		if (isCrowdedPos.bottom) possiblePositionsScoreInc['top'].score -= 1;
		else {
			let topSpaceSoftLimit = this.isFixedPos
				? ((this.targetBB.top - tooltipSizesArrowInc.height) - this.winBoardBB.top)
				: topSpaceHardLimit;
			possiblePositionsScoreInc['top'].score += topSpaceSoftLimit / this.winBoardBB.height;
		}


		/*
		possiblePositionsScoreInc['top'].disabled = ( !this.isFixedPos && this.targetBB.top - tooltipSizesArrowInc.height < maxArea.top);
		if (isCrowdedPos.top) possiblePositionsScoreInc['top'].score -= 1;
		else{
			let topBottomScore = 0;
			if (this.isFixedPos){
				
			}
			else{
				topBottomScore = (this.winBB.height - targetWithTooltipHeightTopBottom) / this.winBB.height;
			}
			

			possiblePositionsScoreInc['top'].score += topBottomScore;
		}*/

		// Check for space to the right
		possiblePositionsScoreInc['right'].disabled = (this.targetBB.right + newTooltipWidthArrowInc.right > maxArea.right + 1); // +1 i don't know why yet
		possiblePositionsScoreInc['right'].score += (isCrowdedPos.right ? -1 : 1 - (newTooltipWidthArrowInc.right / (this.winBB.width - this.targetBB.right)));

		// Check for space to the left
		possiblePositionsScoreInc['left'].disabled = (this.targetBB.left - newTooltipWidthArrowInc.left < maxArea.left);
		possiblePositionsScoreInc['left'].score += (isCrowdedPos.left ? -1 : 1 - (newTooltipWidthArrowInc.left / this.targetBB.left));

		// Check for space inside
		possiblePositionsScoreInc['center'].disabled = this.targetBB.width <= this.tooltipBB.width || minTargetCenterHeight <= this.tooltipBB.height;
		if (this.targetBB.width - this.tooltipBB.width < this.targetBB.height - this.tooltipBB.height) {
			possiblePositionsScoreInc['center'].score += (this.targetBB.width - this.tooltipBB.width) / this.targetBB.width - 0.5;
		}
		else {
			possiblePositionsScoreInc['center'].score += (this.targetBB.height - this.tooltipBB.height) / this.targetBB.height - 0.5;
		}

		const possiblePositionsScoreIncArr = [];
		Object.keys(possiblePositionsScoreInc).forEach((pos) => {
			if (!possiblePositionsScoreInc[pos].disabled) {
				possiblePositionsScoreIncArr.push({
					position: pos,
					score: possiblePositionsScoreInc[pos].score
				});
			}
		});
		possiblePositionsScoreIncArr.sort((a, b) => {
			return b.score - a.score;
		});
		//if (this.options.dpDebugEn) console.log(possiblePositionsScoreIncArr);

		const possiblePositions = possiblePositionsScoreIncArr.map((val) => {
			return val.position;
		});
		//if (this.options.dpDebugEn) console.log(possiblePositions);

		// @var {String}  ex: 'right-aligned'
		var desiredAlignment = (function (pos) {
			var hyphenIndex = pos.indexOf('-');
			if (hyphenIndex !== -1) {
				// has alignment
				return pos.substr(hyphenIndex);
			}
			return '';
		})(desiredTooltipPosition || '');

		// strip alignment from position
		if (desiredTooltipPosition) {
			// ex: "bottom-right-aligned"
			// should return 'bottom'
			desiredTooltipPosition = desiredTooltipPosition.split('-')[0];
		}



		if (possiblePositions.length) {

			if (desiredTooltipPosition !== 'auto') {
				const desiredPosIdx = possiblePositions.indexOf(desiredTooltipPosition);
				if (desiredPosIdx > -1
					&& (
						possiblePositionsScoreIncArr[desiredPosIdx].score > 0
						|| possiblePositionsScoreIncArr[0].score < 0
						|| desiredTooltipPosition === 'center')
				) {
					calculatedPosition.base = desiredTooltipPosition;
				}
				else {
					calculatedPosition.base = possiblePositions[0];
				}

			} else {
				// Pick the first valid position, in order
				calculatedPosition.base = possiblePositions[0];
			}
		}

		// top and bottom alignments
		if (['top', 'bottom'].indexOf(calculatedPosition.base) !== -1) {
			calculatedPosition.align = this.determineTooltipAlignmentX(
				this.targetBB,
				tooltipSizesArrowInc.width,
				desiredAlignment,
				maxArea
			);
		}
		//  left and right positions alignments
		else if (['left', 'right'].indexOf(calculatedPosition.base) !== -1) {
			if (newTooltipWidth[calculatedPosition.base]) calculatedPosition.width = newTooltipWidth[calculatedPosition.base];

			calculatedPosition.align = this.determineTooltipAlignmentY(
				this.targetBB,
				Math.min(tooltipSizesArrowInc.height, SIZE_LIMITS_PX.minTooltipHeight),
				desiredAlignment,
				maxArea
			);

		}
		else if (calculatedPosition.base === 'center') {
			calculatedPosition.align = 'middle';
		}

		//if (this.options.dpDebugEn) console.log(calculatedPosition);

		return calculatedPosition;
	}



	determineTooltipAlignmentX = (targetBB, tooltipWidth, desiredAlignment, maxArea) => {
		const calculateTooltipAlignmentScore = (tooltipLeft, tooltipWidth, maxArea) => {
			const tooltipRight = tooltipLeft + tooltipWidth;
			const overLeft = maxArea.left - tooltipLeft;
			const overRight = tooltipRight - maxArea.right;
			const overLeftCoef = overLeft > 0 ? 2 : 0.5;
			const overRightCoef = overRight > 0 ? 2 : 0.5;
			const score = 1 - (overLeft * overLeftCoef + overRight * overRightCoef) / maxArea.width;
			return score;
		};

		const targetCenterX = targetBB.left + targetBB.width / 2;
		const possibleXAlignments = [
			{ id: 'center', score: calculateTooltipAlignmentScore(targetCenterX - 0.5 * tooltipWidth, tooltipWidth, maxArea) },
			{ id: 'left', score: calculateTooltipAlignmentScore(targetBB.left, tooltipWidth, maxArea) },
			{ id: 'right', score: calculateTooltipAlignmentScore(targetBB.right - tooltipWidth, tooltipWidth, maxArea) },
		];

		possibleXAlignments.sort((a, b) => {
			return b.score - a.score;
		});
		let res = 'center';
		if (possibleXAlignments.length) {

			if (possibleXAlignments.indexOf(desiredAlignment) !== -1) {
				// the desired alignment is valid
				res = desiredAlignment;
			} else {
				// pick the first valid position, in order
				res = possibleXAlignments[0].id;
			}
		}
		if (this.options.dpDebugEn) console.log('[dpit] TOOLTIP X ALIGNMENT:', res, 'Data:', possibleXAlignments);
		return res;
	};


	determineTooltipAlignmentY = (targetBB, tooltipHeight, desiredAlignment, maxArea) => {
		const calculateTooltipAlignmentScore = (tooltipTop, tooltipHeight, maxArea) => {
			const tooltipBottom = tooltipTop + tooltipHeight;
			const overTop = maxArea.top - tooltipTop;
			const overBottom = tooltipBottom - maxArea.bottom;
			const overTopCoef = overTop > 0 ? 2 : 0.5;
			const overBottomCoef = overBottom > 0 ? 2 : 0.5;
			const score = 1 - (overTop * overTopCoef + overBottom * overBottomCoef) / maxArea.height;
			return score;
		};

		const targetCenterY = targetBB.top + targetBB.height / 2;
		const possibleYAlignments = [
			{ id: 'middle', score: calculateTooltipAlignmentScore(targetCenterY - 0.5 * tooltipHeight, tooltipHeight, maxArea) },
			{ id: 'top', score: calculateTooltipAlignmentScore(targetBB.top, tooltipHeight, maxArea) },
			{ id: 'bottom', score: calculateTooltipAlignmentScore(targetBB.bottom - tooltipHeight, tooltipHeight, maxArea) },
		];

		possibleYAlignments.sort((a, b) => {
			return b.score - a.score;
		});
		let res = 'middle';
		if (possibleYAlignments.length) {

			if (possibleYAlignments.indexOf(desiredAlignment) !== -1) {
				// the desired alignment is valid
				res = desiredAlignment;
			} else {
				// pick the first valid position, in order
				res = possibleYAlignments[0].id;
			}
		}
		if (this.options.dpDebugEn) console.log('[dpit] TOOLTIP Y ALIGNMENT:', res, 'Data:', possibleYAlignments);
		return res;
	};


	actualizeOverSizeRadiuses(layerElement, baseLayerClass = CLASSES_CORE.highlight) {
		if (layerElement) {
			layerElement.classList.remove(
				baseLayerClass + MODIFIERS.overLeft,
				baseLayerClass + MODIFIERS.overTop,
				baseLayerClass + MODIFIERS.overRight,
				baseLayerClass + MODIFIERS.overBottom,
			);
			if (this.targetBB.left <= 0) {
				layerElement.classList.add(baseLayerClass + MODIFIERS.overLeft);
			}
			if (this.targetBB.top <= 0) {
				layerElement.classList.add(baseLayerClass + MODIFIERS.overTop);
			}
			if (this.targetBB.right >= this.docBB.right) {
				layerElement.classList.add(baseLayerClass + MODIFIERS.overRight);
			}

			if (this.targetBB.bottom >= this.docBB.bottom) {
				layerElement.classList.add(baseLayerClass + MODIFIERS.overBottom);
			}
		}

	}

	moveLayer2Target = (layer) => {
		if (layer) {
			if (this.isFixedPos) {
				layer.classList.add(CLASSES_CORE.fixedPosition);

			} else {
				layer.classList.remove(CLASSES_CORE.fixedPosition);
			}
			if (this.targetBB) DpitBB.toElement(layer, this.targetBB);
		}
	};

	placeTargetPosBasedLayers(actualizeOverSizeRadiuses = true) {
		this.moveLayer2Target(this.tooltipRefEl);
		this.moveLayer2Target(this.highlightEl);
		if (actualizeOverSizeRadiuses) this.actualizeOverSizeRadiuses(this.highlightEl, CLASSES_CORE.highlight);
	}

	actualizeUserInteraction(stepItem) {
		if (this.userInteraction) {
			this.userInteraction.updateDisableInteractionLayer(
				stepItem ? stepItem : this.stepItem, this.wrapEl,
				this.moveLayer2Target
			);
		}
	}

	isUserInteractionEnabled() {
		return !this.userInteraction?.disableInteractionLayer;
	}

	actualizeTargetItem = (stepItem, isInit = false) => {
		this.isTargetInMobileMenu = false;
		this.isTargetMobileAlt = false;
		if (stepItem) {

			this.isTargetMobileAlt = this.isStepMobileAlt(stepItem);
			// Set stepItem.element
			if (this.isTargetMobileAlt) {
				stepItem.element = documentQuerySelectorSafe(stepItem.alt_mobile_selector);
			}
			else if (stepItem.selector) {
				stepItem.element = documentQuerySelectorSafe(stepItem.selector);
			}
			else {
				stepItem.element = null;
			}
			// Skip not visible steps
			if (!isInit) {
				this.isTargetInMobileMenu = this.isStepItemInMobileMenu(stepItem);
				//this.updateIterateAfter(stepItem);
				if (this.userInteraction) {
					this.userInteraction.update(stepItem);
				}
			}
		}
	};

	clearMobileMenuTarget() {
		this.isTargetInMobileMenu = false;
		this.isTargetMobileAlt = false;
	}

	isStepMobileAlt(stepItem) {
		return this.isMobileSize && stepItem.alt_mobile_selector;
	}

	isStepItemInMobileMenu(stepItem) {
		return this.isStepMobileAlt(stepItem) && stepItem.is_in_mobile_menu === '1';
	}

	skipCurrentStep() {
		// skipping step
		if (!this.isBackward) {
			// skipping forward
			if (this.stepIdx < this.stepItems.length - 1) {
				if (this.stepIdx === 0) this.visibleFirstStepShouldBeEvaluated = true;
				this.goToNextStep();
			}
			else {
				this.visibleLastStepShouldBeEvaluated = true;
				this.goToPrevStep();
			}
		}
		else {
			// skipping backward
			if (this.stepIdx > 0) {
				if (this.stepIdx === this.stepItems.length - 1) this.visibleLastStepShouldBeEvaluated = true;
				this.goToPrevStep();
			}
			else {
				this.visibleFirstStepShouldBeEvaluated = true;
				this.goToNextStep();
			}
		}
	}

	checkTargetElValidity() {
		//const isElementVisible = isElementVisibleParentInc(this.stepItem.element);

		const skipStep = this.shouldBeStepSkipped(this.stepItem, this.isTargetInMobileMenu, true);
		this.setNotVisibleRefBullet(this.stepItem.step, skipStep);
		if (skipStep) {
			this.skipCurrentStep();
			return false;
		}
		this.actualizeDummyMode();

		if (this.visibleLastStepShouldBeEvaluated) {
			this.visibleLastStepIdx = this.stepIdx;
			this.visibleLastStepShouldBeEvaluated = false;
		}
		if (this.visibleFirstStepShouldBeEvaluated) {
			this.visibleFirstStepIdx = this.stepIdx;
			this.visibleFirstStepShouldBeEvaluated = false;
		}
		return true;
	}

	hasTargetItemHiddenOverlay(item) {
		if (item) {
			return item
				&& item.element
				&& item.enable_interaction.val == '1'
				&& !this.isDummyMode()
				&& item.enable_interaction.ext?.hide_overlay == '1';
		}
		return false;
	}


	isHideNextByCurrentStepMeta() {
		return this.stepItem
			&& this.stepItem.element
			&& this.stepItem.enable_interaction.val == '1'
			//&& !this.this.stepItem.isDummyElement
			&& this.stepItem.enable_interaction.ext?.hide_next_btn == '1';
	}


	getIsMobileSize(breakPointOverride = null, winBB = this.winBB) {
		return this.mainCfg.mobile_break_points && (winBB.width <= (breakPointOverride ? breakPointOverride : this.mainCfg.mobile_break_points.mobile));
	}

	enableStepSkippingWhenNotFoundRefEl() {
		this.disabledStepSkippingWhenNotFoundRefEl = false;
	}

	disableStepSkippingWhenNotFoundRefEl() {
		this.disabledStepSkippingWhenNotFoundRefEl = true;
	}

	shouldBeStepSkipped(stepItem, isTargetInMobileMenu, isElementVisible = true) {
		let skipStep = false;
		if (this.options.skip_absent_ref_el && !this.disabledStepSkippingWhenNotFoundRefEl

			&& (stepItem.selector || isTargetInMobileMenu)
			&& (!stepItem.element || (!isElementVisible && !isTargetInMobileMenu))
		) {
			skipStep = true;
		}
		return skipStep;

	}

	isElementInsideDocBoard(element) {
		if (this.docBB && element) {
			const bb = DpitBB.fromElement(element);
			return DpitBB.intersect(bb, this.docBB) !== null;
		}
		return true;
	}

	getHighlightLayerClassName() {
		const res = [CLASSES_CORE.highlight];
		if (typeof (this.options.highlightClass) === 'string' && this.options.highlightClass) {
			res.push(this.options.highlightClass);
		}
		return res.join(' ');
	}

	getStartStepIdx() {
		if (this.visibleFirstStepIdx) return this.visibleFirstStepIdx;
		return 0;
	}

	getEndStepIdx() {
		if (this.visibleLastStepIdx) return this.visibleLastStepIdx;
		return this.stepItems.length - 1;
	}

	getStepContent(stepItem) {

		/*return this.tooltipTextEl.innerHTML = `What is Lorem Ipsum?
		Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book. It has survived not only five centuries, but also the leap into electronic typesetting, remaining essentially unchanged. It was popularised in the 1960s with the release of Letraset sheets containing Lorem Ipsum passages, and more recently with desktop publishing software like Aldus PageMaker including versions of Lorem Ipsum.
		Why do we use it?
		It is a long established fact that a reader will be distracted by the readable content of a page when looking at its layout. The point of using Lorem Ipsum is that it has a more-or-less normal distribution of letters, as opposed to using 'Content here, content here', making it look like readable English. Many desktop publishing packages and web page editors now use Lorem Ipsum as their default model text, and a search for 'lorem ipsum' will uncover many web sites still in their infancy. Various versions have evolved over the years, sometimes by accident, sometimes on purpose (injected humour and the like).
		Where does it come from?
		Contrary to popular belief, Lorem Ipsum is not simply random text. It has roots in a piece of classical Latin literature from 45 BC, making it over 2000 years old. Richard McClintock, a Latin professor at Hampden-Sydney College in Virginia, looked up one of the more obscure Latin words, consectetur, from a Lorem Ipsum passage, and going through the cites of the word in classical literature, discovered the undoubtable source. Lorem Ipsum comes from sections 1.10.32 and 1.10.33 of "de Finibus Bonorum et Malorum" (The Extremes of Good and Evil) by Cicero, written in 45 BC. This book is a treatise on the theory of ethics, very popular during the Renaissance. The first line of Lorem Ipsum, "Lorem ipsum dolor sit amet..", comes from a line in section 1.10.32.
		The standard chunk of Lorem Ipsum used since the 1500s is reproduced below for those interested. Sections 1.10.32 and 1.10.33 from "de Finibus Bonorum et Malorum" by Cicero are also reproduced in their exact original form, accompanied by English versions from the 1914 translation by H. Rackham.
		Where can I get some?
		There are many variations of passages of Lorem Ipsum available, but the majority have suffered alteration in some form, by injected humour, or randomised words which don't look even slightly believable. If you are going to use a passage of Lorem Ipsum, you need to be sure there isn't anything embarrassing hidden in the middle of text. All the Lorem Ipsum generators on the Internet tend to repeat predefined chunks as necessary, making this the first true generator on the Internet. It uses a dictionary of over 200 Latin words, combined with a handful of model sentence structures, to generate Lorem Ipsum which looks reasonable. The generated Lorem Ipsum is therefore always free from repetition, injected humour, or non-characteristic words etc.`;
*/
		return stepItem ? stepItem.intro_proc || stepItem.intro : '';
	}

	setTooltipContent(stepItem) {
		this.tooltipTextEl.innerHTML = this.getStepContent(stepItem);
	}


	actualizeButtonFocus = (isFirstStep, isLastStep) => {
		if (this.options.controlsAutoFocusEnable) {
			let elementToFocusOn = this.isBackward ? this.buttonPrevEl : this.buttonNextEl;
			if (isLastStep) elementToFocusOn = this.buttonCancelEl;
			else if (isFirstStep && this.stepItems.length > 1) elementToFocusOn = this.buttonNextEl;

			if (elementToFocusOn && elementToFocusOn !== document.activeElement) {
				elementToFocusOn.focus({ preventScroll: true, focusVisible: false });
				if (this.options.dpDebugEn) {
					const $buttonTextEl = $(elementToFocusOn).children('.' + CLASSES_CHILD.buttonText);
					if ($buttonTextEl.length)

						console.log('[dpit] AUTO FOCUS: ' + $buttonTextEl.text(), elementToFocusOn);
				}
			}
		}
	};

	activateBullet(idx) {
		$(this.bulletsLayer).find('li.active').removeClass('active');
		$(this.bulletsLayer).find(`li[data-step-number="${idx}"]`).addClass('active').attr('aria-selected', 'true');
	}

	setNotVisibleRefBullet(idx, notVisible = true) {
		if (notVisible) $(this.bulletsLayer).find(`li[data-step-number="${idx}"]`).addClass('not-visible-ref');
		else {
			$(this.bulletsLayer).find(`li[data-step-number="${idx}"]`).removeClass('not-visible-ref');
			if (this.visibleLastStepIdx && idx > this.visibleLastStepIdx) {
				this.visibleLastStepIdx = idx;
			}
			if (this.visibleFirstStepIdx && idx < this.visibleFirstStepIdx) {
				this.visibleFirstStepIdx = idx;
			}
		}
	}

	getNotVisibleStepCnt() {
		return $(this.bulletsLayer).find('li.not-visible-ref').length;
	}

	actualizeProgressBar() {
		const isProgressBarEnabled = this.isProgressBarEnabled();
		if (isProgressBarEnabled) {
			showElement(this.progressLayer);
			const progressVal = this.getProgressInPercent();
			this.progressBarLayer.style.cssText = 'width:' + progressVal + '%;';
			this.progressBarLayer.setAttribute('aria-valuenow', progressVal);
		}
		this.actualizeLayer(isProgressBarEnabled, this.progressLayer);
	}

	isBulletNavig() {
		return this.options.showBullets && this.stepItems.length > 1;
	}

	actualizeBulletsNavigActiveItem() {

		if (this.options.disable_navigation_by_bullets) $(this.bulletsLayer).addClass(CLASSES_CORE.bullets + '--disabled');
		else $(this.bulletsLayer).removeClass(CLASSES_CORE.bullets + '--disabled');

		const isBulletsNavigEnabled = this.isBulletNavig();
		if (isBulletsNavigEnabled) {
			// Adjusting bullets - skipped steps
			if (this.visibleLastStepIdx) {
				for (let i = this.visibleLastStepIdx + 1; i < this.stepItems.length; i++) {
					this.setNotVisibleRefBullet(i + 1, true);
				}
			}
			if (this.visibleFirstStepIdx) {
				for (let i = this.visibleFirstStepIdx - 1; i >= 0; i--) {
					this.setNotVisibleRefBullet(i + 1, true);
				}
			}
			//change active bullet
			if (this.stepItem) this.activateBullet(this.stepItem.step);
		}
		this.actualizeLayer(isBulletsNavigEnabled, this.bulletsLayer);
	}

	actualizeBulletScrollBar(autoScroll = true) {
		let res = false;
		if (this.listInBulletsNavigLayer) {
			if (hasElementScrollBarX(this.listInBulletsNavigLayer)) {
				if (!this.listInBulletsNavigLayer.classList.contains(CLASSES_CORE.hasScrollBar)) {
					res = true;
				}
				this.listInBulletsNavigLayer.classList.add(CLASSES_CORE.hasScrollBar);
				if (autoScroll) {
					const activeEl = this.listInBulletsNavigLayer.querySelector('li.active');
					if (activeEl) {
						scrollIntoView(activeEl, {
							validTarget: target => target === this.listInBulletsNavigLayer,
							time: 300
						});
					}
				}
			}
			else {
				if (this.listInBulletsNavigLayer.classList.contains(CLASSES_CORE.hasScrollBar)) {
					res = true;
				}
				this.listInBulletsNavigLayer.classList.remove(CLASSES_CORE.hasScrollBar);
			}
		}
		return res;
	}

	actualizeSkipButton(buttonSkipLabel) {
		//this.buttonCancelEl.setAttribute('title', '');
		this.buttonSkipTextLayer.innerHTML = buttonSkipLabel;
	}

	actualizeButtons() {
		// Buttons
		let isHideNextByCurrentStepMeta = this.isHideNextByCurrentStepMeta(this.step);
		const isLastStep = this.stepIdx >= this.getEndStepIdx();
		const isFirstStep = this.stepIdx <= this.getStartStepIdx();


		const buttonPrevClass = classNames({
			[this.options.buttonClass]: true,
			[CLASSES_CORE.buttonPrev]: true,
			[CLASSES_CORE.hiddenHard]: this.options.hide_previous_step_button,
			[CLASSES_CORE.disabled]: isFirstStep,
			[CLASSES_CORE.noTrans]: true,
		});
		const buttonNextClass = classNames({
			[this.options.buttonClass]: true,
			[CLASSES_CORE.buttonNext]: true,
			[this.options.buttonClass + MODIFIERS.full]: this.options.hide_previous_step_button,
			[CLASSES_CORE.disabled]: isLastStep || isHideNextByCurrentStepMeta,
			[CLASSES_CORE.noTrans]: true,
		});
		const buttonSkipClass = classNames({
			[this.options.buttonClass]: true,
			[CLASSES_CORE.buttonSkip]: true,
			[CLASSES_CORE.hiddenHard]: this.options.hide_skip_button && !isLastStep,
			[CLASSES_CORE.buttonDone]: isLastStep,
			[CLASSES_CORE.noTrans]: true,
		});
		const buttonSkipLabel = isLastStep ? this.options.labeling.doneLabel : this.options.labeling.skipLabel;

		if (this.buttonPrevEl) {
			this.buttonPrevEl.className = buttonPrevClass;
		}

		if (this.buttonNextEl) {
			this.buttonNextEl.className = buttonNextClass;
		}

		if (this.buttonCancelEl && this.buttonSkipTextLayer) {
			this.buttonCancelEl.className = buttonSkipClass;
			this.actualizeSkipButton(buttonSkipLabel, isLastStep);
		}
		$(`.${CLASSES_CORE.button}`).removeClass(CLASSES_CORE.noTrans);
		this.actualizeButtonFocus(isFirstStep, isLastStep);
	}


	onNavigBulletClick(event) {
		if (event && event.target && !this.options.disable_navigation_by_bullets) {
			this.goToStepByStepLabel(event.target.closest('li').getAttribute('data-step-number'));
		}
	}

	createButtonContent(textContent) {
		let buttonContentLayer = document.createElement('div');
		buttonContentLayer.className = CLASSES_CHILD.buttonText;
		buttonContentLayer.innerHTML = textContent;
		return buttonContentLayer;
	}

	loadIcons() {
		this.loadArrowButtonIcons(this.options.labeling.button_arrows);
		this.loadCancelButtonIcons(this.options.labeling.button_cancel_ico);
	}

	loadArrowButtonIcons(iconOption) {
		switch (iconOption) {
			case 'vector': {

				if (!this.arrowIcoWrapData) {
					this.arrowIcoWrapData = this.appendIcoSpacerToPrevNextButtons(CLASSES_CHILD.buttonIcoWrap);
					import(/* webpackChunkName: "dpitButtonIcoPrevNext" */ './dpitButtonIcoPrevNext').then(svgLoader => {
						const svgStringPrevNext = svgLoader.default(CLASSES_CHILD.buttonIco);
						if (svgStringPrevNext) {
							$(svgStringPrevNext).appendTo(this.arrowIcoWrapData.next);
							$(svgStringPrevNext).prependTo(this.arrowIcoWrapData.prev);
						}
					});
				} else {
					this.showLayers(this.arrowIcoWrapData?.prev, this.arrowIcoWrapData?.next);
					hideElement(this.arrowButtonSpacersData?.prev, false);
					hideElement(this.arrowButtonSpacersData?.next, false);
				}
			}
				break;
			case 'text': {
				const textInButtonClass = CLASSES_CHILD.buttonIcoWrap;
				const textIconClass = textInButtonClass + ' ' + textInButtonClass + MODIFIERS.symbol;
				$('<span class="' + textIconClass + '">&rarr;</span>').appendTo(this.buttonNextEl);
				$('<span class="' + textIconClass + '">&larr;</span>').prependTo(this.buttonPrevEl);
			}
				break;
			default: {
				if (!this.arrowButtonSpacersData) {
					this.arrowButtonSpacersData = this.appendIcoSpacerToPrevNextButtons(CLASSES_CHILD.buttonSpacer);
				} else {
					this.showLayers(this.arrowButtonSpacersData?.prev, this.arrowButtonSpacersData?.next);
				}
				hideElement(this.arrowIcoWrapData?.prev, false);
				hideElement(this.arrowIcoWrapData?.next, false);
			}
		}
	}

	loadCancelButtonIcons(iconOption) {
		switch (iconOption) {
			case 'vector': {
				hideElement(this.cancelButtonSpacerEl, false);
				if (!this.cancelButtonIcoWrapEl) {
					this.cancelButtonIcoWrapEl = this.appendIcoSpacerToCancelButton(CLASSES_CHILD.buttonIcoWrap);
					import(/* webpackChunkName: "dpitButtonIcoCancel" */ './dpitButtonIcoCancel').then(svgLoader => {
						const svgStringCancel = svgLoader.default(CLASSES_CHILD.buttonIco);
						if (svgStringCancel) {
							$(svgStringCancel).appendTo(this.cancelButtonIcoWrapEl);
						}
					});
				}
				else {
					showElement(this.cancelButtonIcoWrapEl);
				}
			}
				break;
			case 'text': {
				const textIconClass = CLASSES_CHILD.buttonIcoWrap + ' ' + CLASSES_CHILD.buttonIcoWrap + MODIFIERS.symbol;
				$('<span class="' + textIconClass + '">&#9932;</span>').prependTo(this.buttonCancelEl);
			}
				break;
			default: {
				hideElement(this.cancelButtonIcoWrapEl, false);
				if (!this.cancelButtonSpacerEl) {
					this.cancelButtonSpacerEl = this.appendIcoSpacerToCancelButton(CLASSES_CHILD.buttonSpacer);
				} else {
					showElement(this.cancelButtonSpacerEl);
				}

			}
		}
	}

	appendIcoSpacerToPrevNextButtons(spacerClass) {
		const buttonSpacers = {
			next: document.createElement('div'),
			prev: document.createElement('div')
		};
		buttonSpacers.next.className = spacerClass;
		$(buttonSpacers.next).appendTo(this.buttonNextEl);

		buttonSpacers.prev.className = spacerClass;
		$(buttonSpacers.prev).prependTo(this.buttonPrevEl);

		return buttonSpacers;
	}

	appendIcoSpacerToCancelButton(spacerClass) {
		const cancelButtonSpacer = document.createElement('div');
		cancelButtonSpacer.className = spacerClass;
		$(cancelButtonSpacer).prependTo(this.buttonCancelEl);
		return cancelButtonSpacer;
	}



	// SECTION INIT
	init() {
		if (!this.initialized) {
			this.initialized = true;
			this.createLayers();
			this.actualizeNumberBB(true);
			this.actualizeStepNumber();
			this.actualizeProgressBar();
			this.actualizeTooltipMaxWidth();
			this.actualizeBulletsNavigActiveItem();
			this.loadIcons();
			this.initUserInteraction();
			this.connectListeners();
			this.doAction(EVENTS.onInit);
		}
	}

	/**
 * Setting anchors to behave like buttons
 *
 * @api private
 * @method _setAsButton
*/
	setAsButton(anchor, label, tabIndex = 0) {
		anchor.setAttribute('role', 'button');
		if (label && typeof label === 'string') anchor.setAttribute('aria-label', label);
		if (tabIndex >= 0) {
			anchor.tabIndex = 0;
		}
	}

	createBullet(step, roleLinkList = true, isActive = false, onClickCallback = null) {
		var innerLi = document.createElement('li');
		if (roleLinkList) {
			innerLi.setAttribute('role', 'tab');
			innerLi.tabIndex = 0;
			innerLi.title = i18n('step', this.mainCfg.i18n) + ' ' + step;
		}

		if (onClickCallback) innerLi.onclick = onClickCallback;

		if (isActive) {
			innerLi.className = 'active';
			innerLi.setAttribute('aria-selected', 'true');
		}
		else {
			innerLi.setAttribute('aria-selected', 'false');
		}

		if (roleLinkList) {
			innerLi.setAttribute('data-step-number', step);
		}

		var innerNumber = document.createElement('div');
		innerNumber.innerHTML = step;
		innerLi.appendChild(innerNumber);

		return innerLi;
	}

	createNavigBullets(className = CLASSES_CORE.bullets, roleLinkList = true, activeIdx = 0, onClickCallback = null) {
		const bulletsLayer = document.createElement('div');
		$(bulletsLayer).addClass(className);
		this.listInBulletsNavigLayer = document.createElement('ul');
		this.listInBulletsNavigLayer.className = classNames([CLASSES_CORE.dpitScrollBar, CLASSES_CORE.dpitScrollBar + '--x']);
		if (roleLinkList) this.listInBulletsNavigLayer.setAttribute('role', 'tablist');


		this.stepItems.forEach((item, i) => {
			this.listInBulletsNavigLayer.appendChild(this.createBullet(item.step, roleLinkList, i === activeIdx, onClickCallback));
		});
		bulletsLayer.appendChild(this.listInBulletsNavigLayer);
		return bulletsLayer;
	}

	createNumberLayer(modifier = '') {
		const numberEl = document.createElement('div');
		numberEl.className = CLASSES_CORE.number;
		if (modifier) numberEl.classList.add(CLASSES_CORE.number + modifier);

		numberEl.setAttribute('aria-label', i18n('aria-main', this.mainCfg.i18n) + ' - ' + i18n('aria-step-num', this.mainCfg.i18n));
		const numberTextEl = document.createElement('div');
		numberTextEl.className = CLASSES_CHILD.numberText;
		numberEl.appendChild(numberTextEl);
		return numberEl;
	}

	createDummyLayer() {
		const targetDummyEl = document.createElement('div');
		targetDummyEl.className = CLASSES_CORE.targetDummy;
		targetDummyEl.setAttribute('aria-hidden', 'true');
		//$(targetDummyEl).on('click', this.onOverlayClick.bind(this));
		return targetDummyEl;
	}

	createLayers() {

		// Flier
		this.flierEl = document.createElement('div');
		this.flierEl.className = CLASSES_CORE.flier;
		this.wrapEl.appendChild(this.flierEl);

		// Highlight
		this.highlightEl = document.createElement('div');

		this.highlightEl.className = classNames(this.getHighlightLayerClassName(), CLASSES_CORE.hidden);
		this.highlightEl.setAttribute('role', 'presentation');
		this.inHighlightLayerAnimation = new BulletsAnimation(
			this.highlightEl,
			CLASSES_CORE.rbAnim + MODIFIERS.inHighlight,
			70,
			10,
			4,
			3
		);

		this.wrapEl.appendChild(this.highlightEl);

		// Tooltip ref.
		this.tooltipRefEl = document.createElement('div');
		this.tooltipRefEl.className = classNames(CLASSES_CORE.tooltipReference, CLASSES_CORE.hidden);
		this.tooltipRefEl.setAttribute('role', 'presentation');
		this.wrapEl.appendChild(this.tooltipRefEl);

		// Tooltip
		this.tooltipEl = document.createElement('div');
		this.tooltipEl.className = CLASSES_CORE.tooltip;
		this.tooltipEl.setAttribute('aria-label', i18n('aria-main', this.mainCfg.i18n));
		this.tooltipEl.setAttribute('aria-hidden', 'false');
		this.tooltipEl.setAttribute('role', 'dialog');
		this.tooltipEl.setAttribute('aria-describedby', CLASSES_CORE.tooltipText);
		this.tooltipRefEl.appendChild(this.tooltipEl);

		// Tooltip border radius padding compensation
		this.tooltipRadiusPaddingEl = document.createElement('div');
		this.tooltipRadiusPaddingEl.className = CLASSES_CORE.tooltipRadiusPadding;
		this.tooltipEl.appendChild(this.tooltipRadiusPaddingEl);

		// Tooltip text
		this.tooltipTextEl = document.createElement('div');
		this.tooltipTextEl.className = classNames([CLASSES_CORE.tooltipText, CLASSES_CORE.dpitScrollBar]);
		this.tooltipTextEl.setAttribute('id', CLASSES_CORE.tooltipText);
		this.tooltipRadiusPaddingEl.appendChild(this.tooltipTextEl);

		// Tooltip arrow
		this.arrowEl = document.createElement('div');
		this.arrowEl.className = CLASSES_CORE.arrow;
		this.arrowEl.setAttribute('role', 'presentation');
		this.tooltipRefEl.appendChild(this.arrowEl);

		// Progress bar
		this.progressLayer = document.createElement('div');
		this.progressLayer.className = CLASSES_CORE.progress;

		this.progressBarLayer = document.createElement('div');
		this.progressBarLayer.className = CLASSES_CORE.progressBar;
		this.progressBarLayer.setAttribute('role', 'progress');
		this.progressBarLayer.setAttribute('aria-valuemin', 0);
		this.progressBarLayer.setAttribute('aria-valuemax', 100);
		this.progressBarLayer.setAttribute('aria-valuenow', this.getProgressInPercent());
		this.progressBarLayer.style.cssText = 'width:' + this.getProgressInPercent() + '%;';
		this.progressLayer.appendChild(this.progressBarLayer);
		this.tooltipRadiusPaddingEl.appendChild(this.progressLayer);

		// Tooltip bullets
		this.bulletsLayer = this.createNavigBullets(
			CLASSES_CORE.bullets,
			true,
			0,
			this.onNavigBulletClick.bind(this)
		);
		this.tooltipRadiusPaddingEl.appendChild(this.bulletsLayer);

		// Tooltip buttons
		this.tooltipButtonsEl = document.createElement('div');
		this.tooltipButtonsEl.className = CLASSES_CORE.tooltipButtons;
		this.tooltipButtonsEl.setAttribute('role', 'navigation');
		this.tooltipButtonsEl.setAttribute('aria-label', i18n('aria-main', this.mainCfg.i18n));
		this.tooltipRadiusPaddingEl.appendChild(this.tooltipButtonsEl);

		//Tooltip number
		this.numberEl = this.createNumberLayer();
		this.tooltipRefEl.appendChild(this.numberEl);

		// Number on sticky tooltip



		//next button
		this.buttonNextEl = document.createElement('div');
		this.buttonNextEl.className = classNames([CLASSES_CORE.button, CLASSES_CORE.buttonNext]);
		this.setAsButton(this.buttonNextEl, this.options.labeling.nextLabel);
		this.buttonNextEl.appendChild(this.createButtonContent(this.options.labeling.nextLabel));

		//previous button
		this.buttonPrevEl = document.createElement('div');
		this.buttonPrevEl.className = classNames([CLASSES_CORE.button, CLASSES_CORE.buttonPrev]);
		this.setAsButton(this.buttonPrevEl, this.options.labeling.prevLabel);
		this.buttonPrevEl.appendChild(this.createButtonContent(this.options.labeling.prevLabel));

		//skip button
		this.buttonCancelEl = document.createElement('div');
		this.buttonCancelEl.className = classNames([CLASSES_CORE.button, CLASSES_CORE.buttonSkip]);
		this.setAsButton(this.buttonCancelEl, this.options.labeling.skipLabel);
		this.buttonSkipTextLayer = this.createButtonContent(this.options.labeling.skipLabel);
		this.buttonCancelEl.appendChild(this.buttonSkipTextLayer);
		this.tooltipButtonsEl.appendChild(this.buttonCancelEl);

		//in order to prevent displaying next/previous button always
		if (this.stepItems.length >= 1) {
			this.buttonPrevNextWrapEl = document.createElement('div');
			this.buttonPrevNextWrapEl.className = CLASSES_CORE.buttonPrevNextWrap;
			this.tooltipButtonsEl.appendChild(this.buttonPrevNextWrapEl);
			this.buttonPrevNextWrapEl.appendChild(this.buttonPrevEl);
			this.buttonPrevNextWrapEl.appendChild(this.buttonNextEl);
		}
		this.initBGOverlayLayer();
	}


	reInitLayers(regularStepChange = true) {
		// Init
		if (regularStepChange) {
			this.highlightEl.className = this.getHighlightLayerClassName();

			// Hide
			if (!IS_DEBUG) this.hideIntroLayers();
		}
		//this.numberEl?.classList.remove(CLASSES_CORE.number + MODIFIERS.onTooltip);
		this.clearFlier();
	}

	// !SECTION INIT


	stopAutoScrollWhenUserAction() {
		this.isUserScrolling = true;
		if (this.userInteractTimeout) clearTimeout(this.userInteractTimeout);
		this.userInteractTimeout = setTimeout(() => {
			this.isUserScrolling = false;
		}, TIMING.userScrolling);
	}



	getFlierBB(targetBB, numberBB, tooltipBB) {
		let res = null;

		if (this.options.scrollTo === 'element') {
			res = DpitBB.merge(targetBB, numberBB);
		}
		else if (this.options.scrollTo === 'tooltip') {
			res = tooltipBB;
		}
		else {
			res = DpitBB.merge(targetBB, tooltipBB, numberBB);
		}
		if (res) {
			if (res.height > this.winBB.height && ['left', 'right', 'center'].includes(this.tooltipPos.base)
			/*|| res.width > this.winBB.width && ['top', 'bottom'].includes(this.tooltipPos.base)*/) {
				res = tooltipBB;
			}
			res = DpitBB.extendByMarginBB(res, this.isFixedPos ? this.windowMargin : this.docMargin);
			//res = DpitBB.intersect(DpitBB.extendByMarginBB(res, this.isFixedPos ? this.windowMargin : this.docMargin), this.docBB);
		}
		return res;
	}





	finalizeStepChange() {
		if (this.options.finalizeStepChangeEn
			&& this.finalizeRefreshCounter < 2
			&& this.flierBB
			&& !this.isFixedPos
			&& this.flierBB.height < this.winBoardBB.height
			&& this.flierBB.width < this.winBoardBB.width
		) {

			this.actualizeFlier();
			if (!this.isBoundingBoxInsideViewPort(this.flierBB, this.isFixedPos, false, 0.9)) {
				this.finalizeRefreshCounter++;

				this.hardRefresh(REFRESH_REASONS.finalize, { useLastTooltipPos: true, useLastNumberPos: false });
				return true;
			}


		}
		return false;
	}

	resetTargetElement = () => {
		if (this.targetBranch) {
			this.targetBranch.stickyPosElements.forEach(stickyElement => {
				stickyElement.removeEventListener(DpitStickyEvents.CHANGE, this.onStickyEvent);
			});
			this.targetBranch.destroy();
			this.targetBranch = null;
		}
	};


	processTargetBranchAndFixedPosState() {
		if (this.targetBranch) this.targetBranch.destroy();

		if (this.stepItem?.element && !this.isDummyMode()) {
			this.targetBranch = new DpitTargetBranch(this.stepItem.element, this.options.dpDebugEn);
			this.targetBranch.process();
		}
		else {
			this.targetBranch = null;
		}
		this.actualizeFixedAndStickyPos(this.stepItem);
	}

	disableStickyEvents = () => {
		if (this.stickyEvents) {
			this.stickyEvents.disableEvents(false);
			this.stickyEvents = null;
		}
	};

	setFixedMode(isFixedPos, dbgEnWhenFalse = false, dbgInfo) {
		this.isFixedPos = isFixedPos;
		if (this.options.dpDebugEn && (isFixedPos || dbgEnWhenFalse)) console.log('[dpit] Fixed mode:', isFixedPos, dbgInfo);
	}

	clearFixedMode() {
		this.isFixedPos = false;
		this.disableStickyEvents();
		this.targetElForSticky = null;
	}

	containsFixedPos() {
		if (this.isDummyMode()) {
			return true;
		}
		else if (this.targetBranch) {
			return this.targetBranch.containsFixed();
		}
		return false;
	}

	actualizeFixedAndStickyPos = (stepItem) => {
		const targetElement = stepItem.element;
		if (!targetElement || this.targetElForSticky === targetElement) return false;
		const containsFixedPosRes = this.containsFixedPos();
		this.setFixedMode(containsFixedPosRes, false, 'Fixed target or it\'s parent');
		if (!containsFixedPosRes && this.targetBranch && this.targetBranch.containsSticky()) {
			const stickyDetLabel = REFRESH_REASONS.stickyDetector.toUpperCase();
			if (this.options.dpDebugEn) console.log(`[dpit] ${stickyDetLabel}: Activated`);
			this.stickyEvents = new DpitStickyEvents();
			this.targetElForSticky = targetElement;
			this.stickyEvents.addStickies(this.targetBranch.stickyPosElements);

			this.targetBranch.stickyPosElements.forEach(stickyElement => {
				stickyElement.addEventListener(DpitStickyEvents.CHANGE, this.onStickyEvent);
			});
		}
	};

	onStickyEvent = (event) => {
		//if (stepItem === this.stepItem) {
		const prevIsFixedPos = this.isFixedPos;
		const isFixedPos = event.detail.isSticky ? true : this.stickyEvents.isSomeSticky();
		if (prevIsFixedPos !== isFixedPos) {
			const stickyDetLabel = REFRESH_REASONS.stickyDetector.toUpperCase();
			this.setFixedMode(isFixedPos, false, `${stickyDetLabel}: Element > `, this.targetElForSticky, event.detail);
			this.softRefresh(REFRESH_REASONS.stickyDetector);
		}
		//}
	};


	// targetElm can be body
	initBGOverlayLayer(hidden = true) {

		if (!this.overlayContainer) {

			this.overlayContainer = document.createElement('div');
			this.overlayContainer.className = CLASSES_CORE.overlayContainer;
			if (hidden) this.overlayContainer.classList.add(CLASSES_CORE.hiddenKeepEvents);

			this.overlayEl = document.createElement('div');
			this.overlayEl.className = CLASSES_CORE.overlay;

			this.overlayContainer.appendChild(this.overlayEl);
			this.overlayContainer.setAttribute('aria-hidden', 'true');
			this.wrapEl.appendChild(this.overlayContainer);

			this.overlayContainerTransitionDelayMs = getTransitionDurationMs(this.overlayContainer);

			this.initBulletsAnimation(this.overlayContainer);
		}
	}

	initBulletsAnimation(bulletRootElement) {
		this.bulletsAnimation = new BulletsAnimation(bulletRootElement);
	}

	clearFlier() {
		hideElement(this.flierEl, false);
		this.flierBB = null;
	}

	actualizeFlier(omitNumberEl = false) {
		let numberBB = null;
		if (this.options.showStepNumbers === true && !omitNumberEl) {
			numberBB = this.numberBB;
		}

		this.flierBB = this.getFlierBB(this.targetBB, numberBB, this.tooltipBB);
		if (this.flierBB) {
			DpitBB.toElement(this.flierEl, this.flierBB);
			showElement(this.flierEl);
		}
	}

	calculateScrollTimeToTarget() {
		let res = 300;
		if (!this.isFixedPos && this.stepItem?.element) {
			const targetOffset = $(this.stepItem.element).offset();
			const startOffset = { left: $(window).scrollLeft(), top: $(window).scrollTop() };
			const diff = Math.max(
				Math.abs(startOffset.left - targetOffset.left),
				Math.abs(startOffset.top - targetOffset.top)
			);
			res = Math.round(Math.min(
				this.options.maxScrollSpeedPerPageMs,
				Math.max(350, (diff / this.winBB.height) * this.options.scrollSpeedPerPageMs)
			));
			if (this.options.dpDebugEn) console.log('[dpit] SCROLL', {
				'Scroll time': res + ' ms',
				maxScrollSpeedPerPageMs: this.options.maxScrollSpeedPerPageMs,
				scrollSpeedPerPageMs: this.options.scrollSpeedPerPageMs
			});
		}
		return res;
	}

	getScrollSpeedOptions(scrollSpeedOption) {
		const scrollSpeedOptionSanitized = Math.max(Math.min(scrollSpeedOption, 100), 0);
		const scrollSpeedPerPageMs = scrollSpeedOptionSanitized === 100
			? 0
			: SCROLL_SPEED.logCoef * Math.log(scrollSpeedOptionSanitized) + SCROLL_SPEED.constant;

		return {
			scrollSpeedPerPageMs: scrollSpeedPerPageMs,
			maxScrollSpeedPerPageMs: scrollSpeedPerPageMs * SCROLL_SPEED.maxCoef
		};
	}

	actualizeScrollSpeedOptions(scrollSpeedOption) {
		this.setOptions(this.getScrollSpeedOptions(scrollSpeedOption));
	}

	forceScrollTopOffset() {
		return false;
	}

	getScrollFlierOptions(scrollTimeMs = 500) {

		const getFlierPureWidth = () => this.flierBB.width - this.docMargin.left - this.docMargin.right;
		const getFlierPureHeight = () => this.flierBB.height - this.docMargin.top - this.docMargin.bottom;
		const res = {
			align: {
				top: 0.5,
				left: 0.5,
				lockX: !isElementScrollableX(document.documentElement)
			},
			time: scrollTimeMs
		};

		if (this.flierBB) {
			if (this.flierBB.height > this.winBoardBB.height || this.forceScrollTopOffset()) {
				switch (this.tooltipPos.base) {
					case 'top': res.align.top = 0; break;
					case 'bottom':
						res.align.top = 0;
						res.align.topOffset = this.winBoardBB.height - getFlierPureHeight();
						break;
				}
			}
			if (this.flierBB.width > this.winBoardBB.width) {
				switch (this.tooltipPos.base) {
					case 'left': res.align.left = 0; break;
					case 'right':
						res.align.left = 0;
						res.align.leftOffset = this.winBoardBB.width - getFlierPureWidth();
						break;
				}
			}
		}
		return res;
	}

	async scrollFlierIntoView(backward, onlyWhenNeeded = true, scrollTimeMs = TIMING.defaultFlierScroll) {
		this.actualizeFlier();
		if (!this.isFixedPos
			&& this.flierEl
			&& (!onlyWhenNeeded || !this.isBoundingBoxInsideViewPort(this.flierBB, this.isFixedPos, true))) {
			const scrollOptions = this.getScrollFlierOptions(scrollTimeMs);
			this.isAutoScrolling = true;
			this.initScrollFlierTime = Date.now;
			/*const res = */await scrollIntoView(this.flierEl, scrollOptions);
			this.isAutoScrolling = false;
			this.onFlierScrolledInView(backward, this.initScrollFlierTime);
			//return res;
		}
		else {
			this.afterStepChanged(backward);
			//return Promise.resolve(false);
		}
	}


	getScrollTargetOptions(scrollTimeMs) {
		const firstScrollableChildFromClosestFixedParent = this.targetBranch?.getFirstScrollableChildFromClosestFixedParent();
		return {
			time: scrollTimeMs,
			validTarget: (target) => {
				const res = !firstScrollableChildFromClosestFixedParent
					|| firstScrollableChildFromClosestFixedParent === target
					|| $(firstScrollableChildFromClosestFixedParent).find(target).length;
				return res;
			}
		};
	}

	scrollTargetIntoView(scrollTimeMs = TIMING.defaultTargetScroll) {
		if (this.stepItem?.element && this.targetBranch?.containsScrollable()) {
			//this.softenOverlayOpacity();
			//const res = 
			//this.resumeFullOverlayOpacity();
			return scrollIntoView(this.stepItem.element, this.getScrollTargetOptions(scrollTimeMs));
		}
		return Promise.resolve(false);
	}

	getProgressInPercent() {
		// Steps are 0 indexed
		const currentStep = parseInt((this.stepIdx + 1), 10);
		return ((currentStep / (this.stepItems.length - this.getNotVisibleStepCnt())) * 100);
	}

	afterStepChanged(backward) {
		if (this.debugVisualizer) {
			this.debugVisualizer.visualizeStepData(this.targetBB, this.flierBB, this.isFixedPos);
		}
		this.fireAfterStepChangeEvent(backward);
	}

	onFlierScrolledInView(backward, initScrollTime) {
		let willBeRefreshed = false;
		if (/* is not another step */ initScrollTime === this.initScrollFlierTime) {
			willBeRefreshed = this.finalizeStepChange();
			this.initScrollFlierTime = null;
		}
		if (!this.ending && !willBeRefreshed) {
			this.afterStepChanged(backward);
		}
	}

	fireAfterStepChangeEvent(backward) {
		this.doAction(EVENTS.afterStepChange, {
			backward: backward,
			currentStepObj: this.stepObject4Hooks(this.stepItem)
		});
	}


	getMinimalTooltipWidth() {
		const minWidth = $(this.buttonCancelEl).outerWidth()
			+ $(this.buttonPrevNextWrapEl).outerWidth(true)
			+ this.tooltipPadding.left
			+ this.tooltipPadding.right
			+ this.radiusPaddingFix?.left || 0
			+ this.radiusPaddingFix?.right || 0;
		return isNaN(minWidth) ? 300 /* Default 300px*/ : minWidth;
	}

	actualizeButtonsTextVisibility(hideText = false) {
		const buttonNoTextClass = CLASSES_CORE.button + MODIFIERS.noText;
		if (!hideText) {

			if (this.buttonPrevEl.classList.contains(buttonNoTextClass)
				|| this.buttonNextEl.classList.contains(buttonNoTextClass)) {
				if (this.options.labeling.button_arrows === 'none') {
					this.loadArrowButtonIcons('none');
				}
				this.buttonNextEl.classList.remove(buttonNoTextClass);
				this.buttonPrevEl.classList.remove(buttonNoTextClass);
			}
			if (this.buttonCancelEl.classList.contains(buttonNoTextClass)) {
				if (this.options.labeling.button_cancel_ico === 'none') {
					this.loadCancelButtonIcons('none');
				}
				this.buttonCancelEl.classList.remove(buttonNoTextClass);
			}


		}
		else {
			if (!this.buttonPrevEl.classList.contains(buttonNoTextClass)) {
				this.buttonPrevEl.classList.add(buttonNoTextClass);
				if (this.options.labeling.button_arrows === 'none') {
					this.loadArrowButtonIcons('vector');
				}
			}
			if (!this.buttonNextEl.classList.contains(buttonNoTextClass)) {
				this.buttonNextEl.classList.add(buttonNoTextClass);
				if (this.options.labeling.button_arrows === 'none') {
					this.loadArrowButtonIcons('vector');
				}
			}
			if (!this.buttonCancelEl.classList.contains(buttonNoTextClass)) {
				this.buttonCancelEl.classList.add(buttonNoTextClass);
				if (this.options.labeling.button_cancel_ico === 'none') {
					this.loadCancelButtonIcons('vector');
				}
			}
		}


	}

	getMaximalTooltipWidth() {
		return Math.max(this.winBoardBB.width - 20, 20); // number on tooltip etc.
	}


	actualizeTooltipMinWidth() {
		if (this.buttonPrevNextWrapEl) {
			this.buttonPrevNextWrapEl.classList.remove(CLASSES_CORE.buttonPrevNextWrap + MODIFIERS.vert);
			this.actualizeButtonsTextVisibility(false);

			this.minTooltipWidth = this.getMinimalTooltipWidth();
			const maxTooltipWidth = this.getMaximalTooltipWidth();
			if (this.minTooltipWidth > maxTooltipWidth) {

				this.actualizeButtonsTextVisibility(true);
				this.minTooltipWidth = this.getMinimalTooltipWidth();
				if (this.minTooltipWidth <= maxTooltipWidth) {
					updateCssVar('--dpit-min-tooltip-width', round1dec(this.minTooltipWidth) + 'px');
					return;
				}

				this.buttonPrevNextWrapEl.classList.add(CLASSES_CORE.buttonPrevNextWrap + MODIFIERS.vert);
				this.minTooltipWidth = this.getMinimalTooltipWidth();
				updateCssVar('--dpit-min-tooltip-width', round1dec(Math.min(this.minTooltipWidth, maxTooltipWidth)) + 'px');

			}
			else {
				updateCssVar('--dpit-min-tooltip-width', round1dec(this.minTooltipWidth) + 'px');
			}
		}
	}


	actualizeTooltipMaxWidth() {
		if (this.tooltipEl) {
			const maxTooltipWidth = this.getMaximalTooltipWidth();
			if (this.options.max_tooltip_width
				&& !isNaN(this.options.max_tooltip_width)
				&& (!this.winBoardBB || this.options.max_tooltip_width < maxTooltipWidth)
			) {
				this.tooltipEl.style.maxWidth = this.options.max_tooltip_width + 'px';
			}
			else {
				this.tooltipEl.style.maxWidth = '';
			}
		}
	}

	async tryUnlockActualizeTarget(processFce, processImmediatelyWhenReady = false) {
		let res = RES_PROCESS_ASYNC.abort;
		if (!this.actualizeTargetLock) {
			if (processFce) {
				this.lockActualizeTarget();
				res = await processFce();
				this.unlockActualizeTarget();
			}
		} else {
			if (processImmediatelyWhenReady) {
				res = RES_PROCESS_ASYNC.deferred;
				this.processFceToRun = processFce;
			}
		}
		return res;
	}

	unlockActualizeTarget() {
		this.actualizeTargetLock = false;
		if (this.processFceToRun) {
			const fceBkp = this.processFceToRun;
			this.processFceToRun = null;
			this.tryUnlockActualizeTarget(fceBkp, false);
		}
	}

	lockActualizeTarget() {
		this.actualizeTargetLock = true;
	}

	processOnBeforeShowStep() {
		if (this.onBeforeShowStepCallback instanceof Function && this.stepItem) {
			return this.onBeforeShowStepCallback(this.stepItem);
		}
		return Promise.resolve(RES_PROCESS_ASYNC.continue);
	}

	printDbgStepInfo(reason, dbgInfo) {
		if (this.options.dpDebugEn) console.log(`[dpit] ====== STEP ${this.stepItem.step} - ${reason.toUpperCase()}`, dbgInfo, 'Step item {', this.stepItem, '}');
	}

	isFlierOnScreen() {
		return true;
	}


	isBoundingBoxInsideViewPort(bbToTest, isFixed, useBoardSize = false, thresholdRatio = 1) {
		const containerBB = useBoardSize ? this.winBoardBB : this.winBB;
		return isBoundingBoxInsideContainer(bbToTest, containerBB, isFixed, thresholdRatio);
	}

	finalizeFollowTarget(reason = REFRESH_REASONS.followTarget) {
		this.isFollowTargetInAction = false;
		this.actualizeNumberBB();
		if (this.options.dpDebugEn) console.log('[dpit] FOLLOW TARGET: Stopped - target BB:', this.targetBB);
		if (this.didOrientationChanged) {
			/* Window orientation has changed during follow */
			// is refreshed by resize refresh alg
			//this.hardRefresh(reason);
		} else if (!this.isBoundingBoxInsideViewPort(this.flierBB, this.isFixedPos, false, 0.98) && !this.isUserScrolling) {
			this.placeTooltip(false, true);
			if (!this.isBoundingBoxInsideViewPort(this.flierBB, this.isFixedPos, false, 0.98)) {
				this.hardRefresh(reason);
			}
		}
		else if (this.options.showStepNumbers && this.numberBB && !this.isBoundingBoxInsideViewPort(this.numberBB, this.isFixedPos, false, 0.98)) {
			this.initTooltip();
			this.placeTooltip();
		} else {
			this.hardRefresh(reason);
		}
		this.restartFollowTarget();// restart with lower interval time to avoid high CPU load



	}


	finalizeSoftRefresh(reason, mode = {}) {
		const startStepIdx = this.stepIdx;
		if (this.softRefreshTimer) clearTimeout(this.softRefreshTimer);
		this.softRefreshTimer = setTimeout(() => {
			if (startStepIdx === this.stepIdx) {
				switch (reason) {
					case REFRESH_REASONS.resize:
						if (this.options.dpDebugEn) console.log('[dpit] SOFT RESIZE: Ended');
						this.hardRefresh(reason, mode);
						break;
					case REFRESH_REASONS.followTarget:
						if (!this.resizeTimeout) {
							this.finalizeFollowTarget();
						}
						break;
				}
			}
		}, TIMING.finalizeSoftRefresh);
	}

	showStep(mode = {}, dbgInfo = '') {
		//this.resetCore(false);
		return this.regularStepChange(REFRESH_REASONS.regular, mode, dbgInfo);
	}

	placeTooltipInSoftRefresh() {
		if (!DpitBB.isInside(this.tooltipBB, this.docBoardBB)) {
			this.placeTooltip();
		} else {
			this.placeTooltip(true, true); // do not iterate tooltip position
		}
	}


	async softRefresh(reason = REFRESH_REASONS.unknown, mode = {}) {
		if (!this.stepItem) return RES_PROCESS_ASYNC.noData;
		return await this.tryUnlockActualizeTarget(() => {
			this.pauseFollowTarget();
			this.calculateTargetBBs();
			this.actualizeUserInteraction(this.stepItem);
			this.placeTargetPosBasedLayers();
			if (this.userInteraction) {
				this.userInteraction.update(this.stepItem);
			}
			this.initTooltip(false);
			this.placeTooltipInSoftRefresh();
			this.finalizeSoftRefresh(reason, mode);
			this.restoreFollowTarget();
			return RES_PROCESS_ASYNC.done;
		}, false);
	}



	async hardRefresh(reason = REFRESH_REASONS.unknown, mode = {}, dbgInfo = '') {
		if (!this.stepItem) return RES_PROCESS_ASYNC.noData;

		return await this.tryUnlockActualizeTarget(async () => {
			//this.restoreFollowTarget();
			const doNotScroll = mode?.resizeType && [
				RESIZE_TYPE.minorAreaChange,
				RESIZE_TYPE.mobUrlBarVisibilityToggle
			].includes(mode.resizeType);
			this.actualizeTargetItem(this.stepItem);
			this.resumeFullOverlayOpacity();
			if (await this.processOnBeforeShowStep() === RES_PROCESS_ASYNC.abort) {
				return RES_PROCESS_ASYNC.abort;
			}
			if (!this.checkTargetElValidity()) return RES_PROCESS_ASYNC.skip;
			this.resetCore(false, false, true);
			//this.pauseFollowTarget();
			this.printDbgStepInfo(reason, dbgInfo);
			this.processTargetBranchAndFixedPosState();
			this.reInitLayers(false);
			let scrollTimeMs = TIMING.defaultFlierScroll;
			if (!doNotScroll) {
				scrollTimeMs = this.calculateScrollTimeToTarget();
				await this.scrollTargetIntoView(Math.min(scrollTimeMs, SCROLL_SPEED.maxParentScrollTimeMs));
			}

			this.actualizeShadowOverlay();
			this.calculateTargetBBs();
			this.actualizeUserInteraction(this.stepItem);
			this.placeTargetPosBasedLayers();
			this.initTooltip(true);
			this.placeTooltip(mode.useLastTooltipPos, mode.useLastNumberPos);
			this.showIntroLayers();
			if (!doNotScroll) this.scrollFlierIntoView(this.isBackward, mode.scrollOnlyWhenNeeded, scrollTimeMs);
			this.restoreFollowTarget();
			//this.restoreRefreshers();
			return RES_PROCESS_ASYNC.done;
		}, true);
	}

	async regularStepChange(reason = REFRESH_REASONS.regular, mode = {}, dbgInfo = '') {

		if (!this.stepItem) return RES_PROCESS_ASYNC.noData;
		mode = { ...mode, scrollOnlyWhenNeeded: false };
		const stepItem = this.stepItem;
		return await this.tryUnlockActualizeTarget(async () => {
			this.resetTargetElement();
			this.actualizeTargetItem(stepItem);
			this.resumeFullOverlayOpacity();
			if (await this.processOnBeforeShowStep() === RES_PROCESS_ASYNC.abort) {
				return RES_PROCESS_ASYNC.abort;
			}
			if (!this.checkTargetElValidity()) return RES_PROCESS_ASYNC.skip;
			this.resetCore(false);
			this.printDbgStepInfo(reason, dbgInfo);

			this.processTargetBranchAndFixedPosState();
			this.reInitLayers();
			const scrollTimeMs = this.calculateScrollTimeToTarget();
			await this.scrollTargetIntoView(Math.min(scrollTimeMs, SCROLL_SPEED.maxParentScrollTimeMs));
			this.actualizeShadowOverlay();
			this.actualizeStepNumber();
			this.calculateTargetBBs();
			this.actualizeUserInteraction(stepItem);
			this.setTooltipContent(stepItem);
			this.placeTargetPosBasedLayers();

			this.actualizeButtons();
			this.actualizeProgressBar();
			this.actualizeTooltipRadiusPadding();
			this.actualizeTooltipMinWidth();

			this.actualizeBulletsNavigActiveItem();

			//this.actualizeTooltipBB();

			this.initTooltip(true);
			this.placeTooltip(mode.useLastTooltipPos, mode.useLastNumberPos);

			this.showIntroLayers();
			this.scrollFlierIntoView(this.isBackward, mode.scrollOnlyWhenNeeded, scrollTimeMs);
			// set intersection observer to check target is on screen
			//this.restoreRefreshers();
			this.startRefreshers();

			return RES_PROCESS_ASYNC.done;

		}, true);

	}

}
